import type { ReportFormatType } from "../types/linehaul/reportFormat.type";
import { LaneType } from "../types/outbound/lane.type";

// Cspell:ignore shipmentsbyid
const base = {
  routes: "operations/routes/routes-api/routes",
  routesTrapsV2: "operations/routes/routes-api/v2",
  routesV3: "operations/routes/routes-api/v3",
  zones: "operations/routes/routes-api/zones",
  driver: "operations/driver",

  equipment: "operations/equipment-api/equipments",
  accessControl: "/access-control",
  roles: "users-api/roles",
  shipment: "operations/shipment-api",
  inboundPlanning: "operations/inbound-planning/inbound-planning-api",
  serviceCenter: "operations/service-center",
  outbound: "operations/outbound",
  masterScheduleBaseUrl: "linehaul/api/v1/master-schedule",
  masterScheduleTemplateBaseUrl: "linehaul/api/v1/master-schedule-template",
  loadboardBaseUrl: "linehaul/api/v1/loadboard",
  loadBoardScheduleBaseUrl: "linehaul/api/v1/load-board-schedule",
  loadboardV2BaseUrl: "linehaul/api/v2/loadboard",
  lane: "lane-api",
  splitIoApi: "https://sdk.split.io/api",
  linehaul: "linehaul/api/v1",
  linehaulV2: "linehaul/api/v2",
  reports: "operations/reports-api",
  dispatch: "dispatch/api",
  common: "operations/common",
  backOffice: "backoffice/secured/api/v1",
  equipmentInventory: "operations/common/equipment",
  tripList: "operations/common/trip",
  dockAuto: "operations/dockauto/v2/api",
};

export const apiUrls = {
  // Route
  getRoutesAndTraps: base.routes,
  getRouteById: base.routes,
  getRoutesAndTrapsV2: `${base.routesTrapsV2}/routes`,
  getRoutesV3: `${base.routesV3}/routes`,
  putRoutes: (routeId: number) => `${base.routes}/${routeId}`,
  postRoutes: base.routes,
  deleteRoute: (routeId: number) => `${base.routes}/${routeId}`,
  patchRoute: (routeId: number) => `${base.routes}/${routeId}`,
  getZipCodesGeolocationByRouteIds: (routeIds: number[]) =>
    `${base.routes}/zipcodes?routeIds=${routeIds.join(",")}`,
  validateZipCodes: `${base.routes}/zipcodes/validate`,
  moveZipCodes: `${base.routes}/zipcodes/move`,
  moveZipCodesV2: `${base.routesTrapsV2}/zipcodes/move`,
  createZipCode: `${base.routesTrapsV2}/zipcodes`,
  updateAndDeleteZipcode: (zipcodeId: number) =>
    `${base.routesTrapsV2}/zipcodes/${zipcodeId}`,
  getAvailableTags: (zipCode: string) =>
    `${base.routesTrapsV2}/zipcodes/${zipCode}/available-tags`,

  // BackOffice API
  getCommodityClasses: () => `${base.backOffice}/commodity-classes`,
  calculateRater: `${base.backOffice}/rater/calculate`,
  sendQuoteEmail: `${base.backOffice}/quick-quote/send-email`,
  getRuleVersions: `${base.backOffice}/rules/versions`,
  getRuleAccessorialHeaders: `${base.backOffice}/rules/accessorial-headers`,
  getPickupTypes: `${base.backOffice}/clients/lookups/pickup-types`,
  getOriginServiceCenter: `${base.backOffice}/clients/lookups/origin-service-center`,
  getPickupAvailableDays: `${base.backOffice}/clients/lookups/pickup-available-days`,
  getTraceTypes: `${base.backOffice}/clients/lookups/trace-types`,
  getServiceLevels: `${base.backOffice}/clients/lookups/service-levels`,
  getSpecialShipmentTagTypes: `${base.backOffice}/clients/lookups/special-shipment-tags-types`,
  getClientAccessorialTypes: (accessorialType: string) =>
    `${base.backOffice}/clients/lookups/accessorial-types?accessorial_type=${encodeURIComponent(accessorialType)}`,
  getHandlingTypes: `${base.backOffice}/clients/lookups/handling-types`,
  getFreightClasses: `${base.backOffice}/clients/lookups/freight-classes`,
  getPiecesTypes: `${base.backOffice}/clients/lookups/pieces-types`,
  getNmfcCodes: `${base.backOffice}/clients/lookups/nmfc`,
  getNmfcSubs: (nmfcItem: string) =>
    `${base.backOffice}/clients/lookups/nmfc/${nmfcItem}/subs`,
  searchNmfcArticles: `${base.backOffice}/pickups/nmfta/search`,
  getNmfcArticleDetails: `${base.backOffice}/pickups/nmfta/article`,
  calculateFreightDensity: `${base.backOffice}/clients/freight/calculate`,
  checkLaneServiceability: `${base.backOffice}/pickups/serviceability-check`,
  zipServiceability: `${base.backOffice}/pickups/validate-zip`,
  validatePickup: `${base.backOffice}/pickups/validate`,
  getAllPickups: `${base.backOffice}/pickups/list`,
  createPickup: `${base.backOffice}/pickups`,
  reschedulePickup: `${base.backOffice}/pickups/reschedule`,
  updatePickup: (pickupNumber: string) =>
    `${base.backOffice}/pickups/${encodeURIComponent(pickupNumber)}`,
  getPickupDetails: (pickupNumber: string) =>
    `${base.backOffice}/pickups/${encodeURIComponent(pickupNumber)}`,
  initiatePickupDocumentUpload: `${base.backOffice}/pickups/documents/upload`,
  confirmPickupDocumentUpload: (documentId: number) =>
    `${base.backOffice}/pickups/documents/upload/confirm?documentId=${documentId}`,
  deletePickupDocument: (documentId: number) =>
    `${base.backOffice}/pickups/documents/delete?documentId=${documentId}`,
  downloadPickupDocument: (
    documentId: number,
    disposition: "inline" | "attachment"
  ) =>
    `${base.backOffice}/pickups/documents/download?documentId=${documentId}&disposition=${disposition}`,
  getBlindShipmentEmailDetails: (shipmentId: string) =>
    `${base.backOffice}/pickups/blind-shipment-email-details?shipmentId=${encodeURIComponent(shipmentId)}`,
  getShipmentEmailDetails: (shipmentId: string) =>
    `${base.backOffice}/pickups/shipment-email-details?shipmentId=${encodeURIComponent(shipmentId)}`,
  sendBlindShipment: `${base.backOffice}/pickups/send-blind-shipment`,
  sendShipmentDetails: `${base.backOffice}/pickups/send-shipment-details`,
  getBillOfLadingEmailDetails: (shipmentId: string) =>
    `${base.backOffice}/pickups/bill-of-lading-email-details?shipmentId=${encodeURIComponent(shipmentId)}`,
  sendBillOfLading: `${base.backOffice}/pickups/send-bill-of-lading`,
  getShippingLabelsEmailDetails: (shipmentId: string) =>
    `${base.backOffice}/pickups/shipping-labels-email-details?shipmentId=${encodeURIComponent(shipmentId)}`,
  sendShippingLabels: `${base.backOffice}/pickups/send-shipping-labels`,
  downloadShippingLabels: (shipmentId: number) =>
    `${base.backOffice}/pickups/shipping-labels/download?shipmentId=${encodeURIComponent(String(shipmentId))}`,
  cancelShipment: `${base.backOffice}/pickups/cancel`,
  estimatedTransitTime: `${base.backOffice}/pickups/estimated-transit-time`,
  getCargoClaimsProValidation: (proNumber: string) =>
    `${base.backOffice}/cargo-claims/pro/validate?proNumber=${encodeURIComponent(proNumber)}`,
  getCargoClaimsStates: `${base.backOffice}/cargo-claims/lookups/states`,
  saveCargoClaimEntry: `${base.backOffice}/cargo-claims/save`,
  initiateCargoClaimDocumentUpload: `${base.backOffice}/cargo-claims/documents/upload`,
  confirmCargoClaimDocumentUpload: (documentId: number) =>
    `${base.backOffice}/cargo-claims/documents/upload/confirm?documentId=${documentId}`,
  deleteCargoClaimDocument: (documentId: number) =>
    `${base.backOffice}/cargo-claims/documents/delete?documentId=${documentId}`,
  getAnalystOpenClaims: `${base.backOffice}/cargo-claims/analyst/open`,
  getFalveyInsuranceDetails: `${base.backOffice}/pickups/falvey-insurance-details`,

  // Route zones
  getZones: base.zones,
  getZoneById: (zoneId: number) => `${base.zones}/${zoneId}`,
  putZone: (zoneId: number) => `${base.zones}/${zoneId}`,
  patchZone: (zoneId: number) => `${base.zones}/${zoneId}`,
  deleteZone: (zoneId: number) => `${base.zones}/${zoneId}`,
  postZones: base.zones,
  postZone: base.zones,

  // Driver
  putDriver: (driverId: number) => `${base.driver}/${driverId}`,
  postDrivers: base.driver,
  getEmployeeDrivers: `${base.driver}/type/Employee`,
  getDriverById: (driverId: number) => `${base.driver}/${driverId}`,
  getDrivers: (serviceCenterCode: number) =>
    `${base.driver}/service-centers/${serviceCenterCode}/drivers`,
  getDriversUsageBySicIdAndPlanDate: (sicId: number, planDate: number) =>
    `${base.driver}/service-centers/${sicId}/plan-date/${planDate}/drivers-usage-info`,
  deleteDriver: (driverId: number) => `${base.driver}/${driverId}`,
  arriveTrip: (tripNumber: number) =>
    `${base.inboundPlanning}/trips/${tripNumber}/arrival`,

  // Equipment
  putEquipment: (equipmentId: number) => `${base.equipment}/${equipmentId}`,
  postEquipments: base.equipment,
  deleteEquipment: (equipmentId: number) => `${base.equipment}/${equipmentId}`,
  getEquipmentById: (equipmentId: number) => `${base.equipment}/${equipmentId}`,
  getEquipmentDetailsBySicId: (sicId: number) =>
    `${base.equipment}/service-center/${sicId}/equipments-details`,
  getEquipmentsBySicIdAndTypeId: (sicId: number, typeId: number) =>
    `${base.equipment}/service-center/${sicId}/equipments/${typeId}`,
  getEquipmentsUsageBySicId: (sicId: number) =>
    `${base.equipment}/service-center/${sicId}/equipments-use-info`,
  getLinehaulTrailers: `${base.linehaul}/equipment`,
  postJunctionSetupConfiguration: (sicCode: string) =>
    `${base.linehaul}/junction-setup/${sicCode}/configuration`,
  getLanesMileageTransitTime: `${base.linehaul}/junction-setup/lanes/mileage-transit-time`,
  getLiveInventory: (sicId: number) =>
    `${base.equipmentInventory}/get-by-sic/${sicId}`,
  searchEquipmentByRefNumber: (refNumber: string) =>
    `${base.equipmentInventory}/search-by-ref/${refNumber}`,
  addEquipmentToInventory: `${base.equipmentInventory}/foreign`,
  deleteLiveInventoryEquipment: (equipmentId: number) =>
    `${base.equipmentInventory}/${equipmentId}`,
  repositionTrailer: (equipmentId: number) =>
    `${base.equipmentInventory}/${equipmentId}/reposition`,
  getEquipmentBySic: (sicId: number) =>
    `${base.equipmentInventory}/get-by-sic/${sicId}`,

  // User
  getUser: (userId: string, include: Readonly<string[]>) => {
    const params = new URLSearchParams();
    include.forEach((element) => {
      params.append("includes", encodeURIComponent(element));
    });
    const paramsString = params.toString() ? `?${params.toString()}` : "";
    return `${base.accessControl}/user/${userId}${paramsString}`;
  },
  getCurrentUser: `${base.accessControl}/user`,

  getUsers: (include: Readonly<string[]>) => {
    const params = new URLSearchParams();
    include.forEach((element) =>
      params.append("includes", encodeURIComponent(element))
    );
    const paramsString = params.toString() ? `?${params.toString()}` : "";
    return `${base.accessControl}/users${paramsString}`;
  },
  getUserGroups: (userId: string) =>
    `${base.accessControl}/user/${userId}/groups`,
  updateUser: `${base.accessControl}/user`,

  // User roles
  getRoles: base.roles,
  putRole: (roleId: number) => `${base.roles}/${roleId}`,
  postRole: base.roles,
  deleteRole: (roleId: number) => `${base.roles}/${roleId}`,

  // Permissions
  getPermissions: "users-api/permissions",
  getPermissionsV2: `${base.accessControl}/permissions`,
  createPermission: `${base.accessControl}/permission`,
  deletePermission: (permissionId: number) =>
    `${base.accessControl}/permission/${permissionId}`,
  updatePermission: (permissionId: number) =>
    `${base.accessControl}/permission/${permissionId}`,

  // Modules
  getModules: `${base.accessControl}/modules`,
  getModule: (moduleId: number) => `${base.accessControl}/module/${moduleId}`,
  deleteModule: (moduleId: number) =>
    `${base.accessControl}/module/${moduleId}`,
  updateModule: (moduleId: number) =>
    `${base.accessControl}/module/${moduleId}`,
  createModule: `${base.accessControl}/module`,

  // Groups
  getGroups: `${base.accessControl}/groups`,
  getGroup: (groupId: number) => `${base.accessControl}/group/${groupId}`,
  createGroup: `${base.accessControl}/group`,
  deleteGroup: (groupId: number) => `${base.accessControl}/group/${groupId}`,
  updateGroup: (groupId: number) => `${base.accessControl}/group/${groupId}`,
  updateGroupPermissions: (groupId: number) =>
    `${base.accessControl}/group/${groupId}/permissions`,
  updateGroupUsers: (groupId: number) =>
    `${base.accessControl}/group/${groupId}/users`,

  getGroupUsers: (groupId: number) =>
    `${base.accessControl}/group/${groupId}/users`,

  // Trip List
  getTripList: (
    sicId: number,
    direction: string,
    dateFrom?: string,
    dateTo?: string,
    removeEnrt?: boolean
  ) => {
    const params = new URLSearchParams({ Direction: direction });
    if (dateFrom) params.set("DateFrom", dateFrom);
    if (dateTo) params.set("DateTo", dateTo);
    if (removeEnrt) params.set("RemoveENRT", "true");
    return `${base.tripList}/get-by-sic/${sicId}?${params.toString()}`;
  },
  searchByBookingNumber: (sicId: number, bookingNumber: string) =>
    `${base.tripList}/${sicId}/search-by-booking-number/${encodeURIComponent(bookingNumber)}`,
  getTripShipments: (sicId: number, tripNumber: number) =>
    `${base.tripList}/${sicId}/get-by-trip-number/${tripNumber}/shipments`,
  getTripByNumber: (sicId: number, tripNumber: number) =>
    `${base.tripList}/${sicId}/get-by-trip-number/${tripNumber}`,
  putDriverCheckIn: (sicId: number, tripNumber: number) =>
    `${base.tripList}/${sicId}/check-in/${tripNumber}`,

  // Shipment
  getUnplannedFreightOD: `${base.shipment}/freight/additional-freight`,
  getShipmentsById: `${base.shipment}/freight/shipmentsbyid`,
  getShipmentsAppointments: (serviceCenterId: number) =>
    `${base.shipment}/freight/shipments/appointments?destinationSicId=${serviceCenterId}`,
  getAppointmentBucketOld: (sicId: number | string, searchTerm = "") =>
    `${base.shipment}/operations/pre-planning/${sicId}/appointments?searchTerm=${searchTerm}`,
  getAppointmentBucketDetails: (sicId: number, bucketName: string) =>
    `${base.shipment}/operations/pre-planning/${sicId}/appointments/${bucketName}/shipments`,
  getAppointmentBucket: (
    sicId: number | string,
    planDate: string,
    searchTerm = ""
  ) =>
    `${base.shipment}/v2/operations/pre-planning/${sicId}/appointments/by-plan-date?planDate=${planDate}&searchTerm=${searchTerm}`,
  getAppointmentRequiredShipments: (sicId: number | string, planDate: string) =>
    `${base.shipment}/v2/operations/pre-planning/${sicId}/aptr-appointments/by-plan-date?planDate=${planDate}`,
  getAppointmentRequiredShipmentDetail: (shipmentId: number | string) =>
    `${base.shipment}/v2/shipments/${shipmentId}/appointment-detail`,
  createAppointment: () => `${base.shipment}/v2/shipments/appointment`,
  getPreviousDeliveriesByConsignee: (consigneeId: number | string) =>
    `${base.shipment}/v2/shipments/consignees/${consigneeId}/previous-deliveries`,
  getCallAttempts: (shipmentId: number | string) =>
    `${base.shipment}/v2/shipments/${shipmentId}/call-attempts`,
  getShipmentBol: (shipmentId: number | string) =>
    `${base.shipment}/v2/shipments/${shipmentId}/bol`,
  createCallAttempt: (shipmentId: number | string) =>
    `${base.shipment}/v2/shipments/${shipmentId}/call-attempts`,
  createProNote: (shipmentId: number | string) =>
    `${base.shipment}/v2/shipments/${shipmentId}/pro-notes`,
  createDrNote: (shipmentId: number | string) =>
    `${base.shipment}/v2/shipments/${shipmentId}/dr-notes`,
  deleteDrNote: (shipmentId: number | string, id: number | string) =>
    `${base.shipment}/v2/shipments/${shipmentId}/dr-notes/${id}`,
  editDrNote: (shipmentId: number | string, id: number | string) =>
    `${base.shipment}/v2/shipments/${shipmentId}/dr-notes/${id}`,
  getAssociatedShipments: (
    shipmentId: number | string,
    sicId: number | string
  ) =>
    `${base.shipment}/v2/shipments/${shipmentId}/associated-shipments?sicId=${sicId}`,
  getSections: (destinationSicId: number) =>
    `${base.shipment}/sections/summary?destinationSicId=${destinationSicId}`,
  getSectionsTableData: `${base.shipment}/sections/summary`,
  getShipmentDetails: `${base.shipment}/shipments/details`,
  getShipmentsByRoute: (trailerId: number | string) =>
    `/${base.inboundPlanning}/delivery-routes/${trailerId}/shipments`,
  getShipmentsByTrap: (trailerId: number | string) =>
    `/${base.inboundPlanning}/delivery-traps/${trailerId}/shipments`,
  // TODO: Note that traps are handled in multiple endpoints. We should resolve.
  getTraps: `${base.shipment}/trap-summary`,
  getAdditionalFreight: `${base.shipment}/freight/additional-freight`,
  getIncompleteTrailer: (date: number, sicId: number) =>
    `/${base.inboundPlanning}/service-centers/${sicId}/dates/${date}/incomplete-trailers`,
  reinstateToPlan: (date: number, sicId: number) =>
    `/${base.inboundPlanning}/service-centers/${sicId}/dates/${date}/incomplete-trailers/move-to-plan`,
  // Maps
  getDataFromMapAttributes: (proximity: string) =>
    `maps/attributes?in=${proximity}`,

  // Outbound Planning
  getLane: (date: number, sicId: number) =>
    `outbound-planning-api/plan/date/${date}/service-center/${sicId}`,

  // Inbound Planning
  addToPlan: (sicId: number, date: number) =>
    `${base.inboundPlanning}/v2/service-centers/${sicId}/dates/${date}/add-to-plan`,
  getDeliveryRoutes: (sicId: number, date: number) =>
    `${base.inboundPlanning}/service-centers/${sicId}/dates/${date}/delivery-routes`,
  getDeliveryRouteStatusOld: (id: number) =>
    `${base.inboundPlanning}/v2/delivery-routes/${id}/available-statuses`,
  getDeliveryTrapStatusOld: (id: number) =>
    `${base.inboundPlanning}/v2/delivery-traps/${id}/available-statuses`,
  getDeliveryRoutesTrapsStatusOldOld: (
    routeIds: number[],
    trapIds: number[]
  ) => {
    const params = new URLSearchParams();
    routeIds.forEach((id) => params.append("routeIds", id.toString()));
    trapIds.forEach((id) => params.append("trapIds", id.toString()));
    return `${
      base.inboundPlanning
    }/delivery-routes-traps/available-statuses?${params.toString()}`;
  },
  getDeliveryRoutesTrapsStatusOld: (routeIds: number[], trapIds: number[]) => {
    const params = new URLSearchParams();
    routeIds.forEach((id) => params.append("routeIds", id.toString()));
    trapIds.forEach((id) => params.append("trapIds", id.toString()));
    return `${
      base.inboundPlanning
    }/v2/delivery-routes-traps/available-statuses?${params.toString()}`;
  },
  getDeliveryRoutesTrapsStatusV3: (routeIds: number[], trapIds: number[]) => {
    const params = new URLSearchParams();
    routeIds.forEach((id) => params.append("routeIds", id.toString()));
    trapIds.forEach((id) => params.append("trapIds", id.toString()));
    return `${base.inboundPlanning}/v3/delivery-routes-traps/available-statuses?${params.toString()}`;
  },
  putDeliveryRouteStatusOld: (id: number) =>
    `${base.inboundPlanning}/delivery-routes/${id}/status`,
  putDeliveryTrapStatusOld: (id: number) =>
    `${base.inboundPlanning}/delivery-traps/${id}/status`,
  putDeliveryRoutesTrapsStatus: `${base.inboundPlanning}/delivery-routes-traps/status`,
  getDispatcherRoutesAvailableStatuses: (routeIds: number[]) => {
    const params = new URLSearchParams();
    routeIds.forEach((id) => params.append("routeIds", id.toString()));
    return `${base.inboundPlanning}/dispatcher-routes/available-statuses?${params.toString()}`;
  },
  putDeliveryRoutesTrapsStatusV2: `${base.inboundPlanning}/v2/delivery-routes-traps/status`,
  createRoute: (unixTime: number, sicId: number) =>
    `${base.inboundPlanning}/routes/date/${unixTime}/service-center/${sicId}`,
  createPickupOnlyRoute: (unixTime: number, sicId: number) =>
    `${base.inboundPlanning}/routes/date/${unixTime}/service-center/${sicId}/pickup-only`,
  getRouteAssignments: (date: number, sicId: number) =>
    `${base.inboundPlanning}/service-centers/${sicId}/dates/${date}/delivery-routes-traps/summary`,
  deleteRouteAssignments: (idsToDelete: string) =>
    `${base.inboundPlanning}/routes/${idsToDelete}`,
  putRouteAssignment: (routeId: number) =>
    `${base.inboundPlanning}/routes/${routeId}`,
  putRouteAssignments: `${base.inboundPlanning}/routes`,
  lockRoutes: `${base.inboundPlanning}/delivery-routes/lock-status`,
  putTrapAssignment: (trapId: number) =>
    `${base.inboundPlanning}/traps/${trapId}`,
  mergeRoutes: (routeId: number) =>
    `${base.inboundPlanning}/routes/${routeId}/merge`,
  unassignShipmentById: `${base.inboundPlanning}/shipments/unassign`,
  getUnassignedShipments: (sicId: number, planDate: number) =>
    `${base.inboundPlanning}/shipments/unassigned/service-center/${sicId}/date/${planDate}`,
  reorderShipments: (routeId: number) =>
    `${base.inboundPlanning}/shipments/order/${routeId}`,
  assignShipmentByRouteId: (routeType: string, routeId: number) =>
    routeType === "delivery_trap"
      ? `${base.inboundPlanning}/shipments/assign-to-trap/${routeId}`
      : `${base.inboundPlanning}/shipments/assign-to-route/${routeId}`,
  removeFromPlan: (sicId: number, date: number) =>
    `${base.inboundPlanning}/remove-from-plan/date/${date}/service-center/${sicId}`,
  // TODO: Unify these?
  getDeliveryRoutesSummary: (sicId: number, date: number) =>
    `${base.inboundPlanning}/service-centers/${sicId}/dates/${date}/delivery-routes/summary`,
  getServiceCenterGeocodeByIds: (sicIds: number[]) =>
    `${base.inboundPlanning}/geocode/sic/${sicIds.join(",")}`,
  createTrap: (sicId: number | string, unixDate: number | string) =>
    `${base.inboundPlanning}/service-centers/${sicId}/dates/${unixDate}/traps`,
  deleteTraps: (trapIds: number[], sicId: number) =>
    `${base.inboundPlanning}/traps?trapIds=${trapIds.join(",")}&sicId=${sicId}`,
  deleteDeliveryTraps: (trapIds: number[]) =>
    `${base.inboundPlanning}/traps/${trapIds.join(",")}`,
  getInboundTraps: (sicId: number | string, date: number | string) =>
    `${base.inboundPlanning}/service-centers/${sicId}/dates/${date}/traps`,
  mergeRoutesAndTraps: `${base.inboundPlanning}/routes-and-traps/merge`,
  postMergeTrap: `${base.inboundPlanning}/traps/merge`,
  reassignShipments: `${base.inboundPlanning}/v2/shipments/re-assign`,
  reassignShipmentsOld: `${base.inboundPlanning}/shipments/re-assign`,
  assignToOsad: `${base.inboundPlanning}/v2/shipments/assign-to-osad`,
  autoSequenceRoutes: (routeIds: number[]) =>
    `${base.inboundPlanning}/delivery-routes/auto-sequencer?${routeIds
      .map((routeId) => `routeIds=${routeId}`)
      .join("&")}`,
  getDeliveryRoutesSequenceStatus: (routeIds: number[] | string) =>
    `${base.inboundPlanning}/delivery-routes/sequence-status?${
      typeof routeIds === "string"
        ? `routeIds=${routeIds}`
        : routeIds.map((routeId) => `routeIds=${routeId}`).join("&")
    }`,

  putMassageRoute: (routeId: number) =>
    `${base.inboundPlanning}/delivery-routes/${routeId}/is-plan-completed`,
  getInboundUrgentAndCriticalCare: (sicCode: number, planDate: number) =>
    `${base.inboundPlanning}/uc-cc-shipments?sicId=${sicCode}&planDate=${planDate}`,
  getUcccOfdShipmentDetails: (shipmentId: number | string) =>
    `${base.linehaul}/uc-cc-ofd/shipment/${shipmentId}`,
  getEntityShipmentDocuments: (shipmentId: number) =>
    `documents/v1/entity/shipment/${shipmentId}?is_deleted=false&limit=100&offset=0`,
  getLCodeCarrierAddendums: (carrierId: number) =>
    `documents/v1/entity/carrier/${carrierId}?is_deleted=false&limit=100&offset=0`,
  getEntityDocument: (documentId: number) =>
    `documents/v1/${documentId}/download`,
  deleteEntityDocument: (documentId: number) =>
    `documents/v1/${documentId}?hard_delete=false`,

  // Operations
  // TODO: This name has "FromOperations" on the end to avoid a naming conflict.
  getAppointmentBucketFromOperations: (date: string) =>
    `/api/operations/pre-planning/${date}/appointment-bucket`,
  getAppointmentBucketShipments: (date: string, id: number | string) =>
    `/api/operations/pre-planning/${date}/appointment-bucket/${id}/shipments`,
  getCarrierContacts: (carrierCode: string) =>
    `/linehaul/api/v1/carrier/${carrierCode}/contacts`,
  getCarrierScacCodes: `${base.linehaul}/carrier/scac-codes`,
  getCaFsc: `${base.linehaul}/settings/rates-accessorials/ca-fsc`,
  updateCaFsc: (id: number) =>
    `${base.linehaul}/settings/rates-accessorials/ca-fsc/${id}`,
  getCaFscDistinctValues: `${base.linehaul}/settings/rates-accessorials/ca-fsc/filters/distinct-values`,
  getRatesAccessorialsCarrier: (carrierId: number) =>
    `${base.linehaul}/settings/rates-accessorials/carrier/${carrierId}`,
  getRatesAccessorialsCarrierAccessorials: (carrierId: number) =>
    `${base.linehaul}/settings/rates-accessorials/carrier/${carrierId}/accessorials`,
  updateLCodeAccessorials: (carrierCode: string) =>
    `${base.linehaul}/settings/rates-accessorials/carrier/${carrierCode}/accessorials`,
  getCarrierLanes: (carrierCode: string) =>
    `${base.linehaul}/settings/rates-accessorials/carrier/${carrierCode}/lanes`,
  addCarrierLaneRate: (carrierCode: string, laneId: number) =>
    `${base.linehaul}/settings/rates-accessorials/carrier/${carrierCode}/lanes/${laneId}/rates`,
  editCarrierLaneRate: (rateId: number) =>
    `${base.linehaul}/settings/rates-accessorials/rates/${rateId}/edit`,
  deleteCarrierLaneRate: (rateId: number) =>
    `${base.linehaul}/settings/rates-accessorials/rates/${rateId}`,

  masterSchedule: {
    createWithTemplate: `${base.masterScheduleBaseUrl}/create-from-template`,
    createWithClone: `${base.masterScheduleBaseUrl}/create-from-clone`,
    createSchedulePair: `${base.masterScheduleBaseUrl}/entry`,
    createTemplateWithTemplate: `${base.masterScheduleTemplateBaseUrl}/clone-template`,
    createTemplateWithClone: `${base.masterScheduleTemplateBaseUrl}/clone-previous-week`,
    createTemplateNew: `${base.masterScheduleTemplateBaseUrl}/create-from-blank`,
    updateTemplate: (id: number) =>
      `${base.masterScheduleTemplateBaseUrl}/${id}`,
    delete: (weekOf: string) => `${base.masterScheduleBaseUrl}/${weekOf}`,
    deleteSchedulePair: (id: number) =>
      `${base.masterScheduleBaseUrl}/entry/${id}`,
    expireSchedulePair: (loadPairScheduleId: number) =>
      `${base.masterScheduleBaseUrl}/entry/expire/${loadPairScheduleId}`,
    getCarriers: "/linehaul/api/v1/carrier",
    get: (week: string) => `${base.masterScheduleBaseUrl}?weekOf=${week}`,
    getSchedulePair: (loadPairId: number | string) =>
      `${base.masterScheduleBaseUrl}/entry/${loadPairId}`,
    getTemplates: base.masterScheduleTemplateBaseUrl,
    getTemplate: (id: number) => `${base.masterScheduleTemplateBaseUrl}/${id}`,
    publish: (weekOf: string) =>
      `${base.masterScheduleBaseUrl}/${weekOf}/publish`,
    batchPublish: `${base.masterScheduleBaseUrl}/entry/publish`,
    getServiceTypes: "/linehaul/api/v1/service-types",
    updateCarrierContacts: (carrierCode: string) =>
      `/linehaul/api/v1/carrier/${carrierCode}/contacts`,
    updateSchedulePair: `${base.masterScheduleBaseUrl}/entry`,
    bulkUpdateSchedules: `${base.masterScheduleBaseUrl}/entries`,
    deleteTemplate: (id: number) =>
      `${base.masterScheduleTemplateBaseUrl}/${id}`,
    deleteTemplateSchedulePair: (templateId: number, loadPairId: number) =>
      `${base.masterScheduleTemplateBaseUrl}/${templateId}/entry/${loadPairId}`,
    getTemplateSchedulePair: (templateId: number, loadPairId: number) =>
      `${base.masterScheduleTemplateBaseUrl}/${templateId}/entry/${loadPairId}`,
    createTemplateSchedulePair: (templateId: number) =>
      `${base.masterScheduleTemplateBaseUrl}/${templateId}/entry`,
    updateTemplateSchedulePair: (templateId: number) =>
      `${base.masterScheduleTemplateBaseUrl}/${templateId}/entry`,
    bulkUpdateTemplateSchedules: `${base.masterScheduleTemplateBaseUrl}/entries`,
  },

  loadBoard: {
    getSchedule: (scheduleId: number) =>
      `${base.loadboardBaseUrl}/schedule/${scheduleId}`,
    getSchedules: (date: string) =>
      `${base.loadboardBaseUrl}/schedules?day=${date}`,
    getShipments: (date: string) =>
      `${base.loadboardBaseUrl}/shipments?day=${date}`,
    getTrips: `${base.loadboardBaseUrl}/trips`,

    getNotes: (scheduleId: number) =>
      `${base.loadboardBaseUrl}/schedule/${scheduleId}/notes`,
    createNote: (scheduleId: number) =>
      `${base.loadboardBaseUrl}/schedule/${scheduleId}/note`,
    updateNote: (noteId: number) => `${base.loadboardBaseUrl}/note/${noteId}`,
    deleteNote: (noteId: number) => `${base.loadboardBaseUrl}/note/${noteId}`,
    getShipmentNotes: (shipmentId: number | string) =>
      `${base.loadboardV2BaseUrl}/shipments/${shipmentId}/notes`,
    getShipmentHistory: (sicCode: string, shipmentId: number | string) =>
      `${base.outbound}/${sicCode}/shipment/${shipmentId}/unplanned-details`,
    createShipmentNote: (shipmentId: number) =>
      `${base.loadboardV2BaseUrl}/shipments/${shipmentId}/notes`,
    updateShipmentNote: (shipmentId: number, noteId: number) =>
      `${base.loadboardV2BaseUrl}/shipments/${shipmentId}/notes/${noteId}`,
    deleteShipmentNote: (shipmentId: number, noteId: number) =>
      `${base.loadboardV2BaseUrl}/shipments/${shipmentId}/notes/${noteId}`,
    publishSchedule: (scheduleId: number) =>
      `${base.loadboardBaseUrl}/schedule/${scheduleId}/publish`,
    unpublishSchedule: (scheduleId: number) =>
      `${base.loadboardBaseUrl}/schedule/${scheduleId}/unpublish`,
    batchPublishSchedules: `${base.loadboardBaseUrl}/schedule/bulk-publish`,
    cancelSchedulePair: `${base.loadboardBaseUrl}/schedule/cancel`,
    reinstateSchedulePair: `${base.loadboardBaseUrl}/schedule/reinstate`,
    reorderTripsForLane: `${base.loadboardBaseUrl}/trip/reorder`,
    getSchedulePair: (loadPairId: string) =>
      `${base.loadboardBaseUrl}/schedule/pair/${loadPairId}`,
    createSchedulePair: `${base.loadboardBaseUrl}/schedule`,
    updateSchedule: (scheduleId: number) =>
      `${base.loadboardBaseUrl}/schedule/${scheduleId}`,
    getChangeRequestLogs: (scheduleId: string) =>
      `${base.loadboardBaseUrl}/schedule/${scheduleId}/change-request-logs`,
    calculateRollover: `${base.loadboardBaseUrl}/calculate-rollover`,
    loadAndHold: (scheduleId: number) =>
      `${base.loadboardBaseUrl}/schedule/${scheduleId}/loadandhold`,
    getScheduleAccessorials: (scheduleId: number) =>
      `${base.loadBoardScheduleBaseUrl}/${scheduleId}/accessorials`,

    // V2
    getShipmentDaySummary: (date: string) =>
      `${base.loadboardBaseUrl}/shipment/${date}`,
    lanesSummary: `${base.loadboardBaseUrl}/lane-summary`,
    daySummary: `${base.loadboardBaseUrl}/day-summary`,
    daySummaryV2: (date: string) =>
      `${base.loadboardV2BaseUrl}/day-summary/${date}`,
    serviceCenterShipmentsByDay: (originSicCode: string, date: string) =>
      `${base.loadboardV2BaseUrl}/service-center-shipments/${originSicCode}/${date}`,
    tripShipments: (tripNumber: number) =>
      `/${base.loadboardV2BaseUrl}/trips/${tripNumber}/shipments`,
    tripList: (date: string) => `${base.linehaulV2}/tripList?day=${date}`,
    transitDashboardTripsInTransit: `${base.linehaul}/transit-dashboard/trips-in-transit`,
    transitDashboardSummary: `${base.linehaul}/transit-dashboard/summary`,
    transitDashboardTripDetails: (tripNumber: number) =>
      `${base.linehaul}/transit-dashboard/trip/${tripNumber}`,
    transitRecentUpdates: `${base.linehaul}/transit-dashboard/recent-updates`,
    transitDashboardNotifications: (
      limit = 100,
      offset = 0,
      timeZone?: string
    ) =>
      `${base.linehaul}/transit-dashboard/notifications?Limit=${limit}&Offset=${offset}${timeZone ? `&Timezone=${encodeURIComponent(timeZone)}` : ""}`,
    transitDashboardMarkNotificationRead: (notificationId: number) =>
      `${base.linehaul}/transit-dashboard/notifications/${notificationId}/mark-read`,
    transitDashboardMarkAllNotificationsRead: `${base.linehaul}/transit-dashboard/notifications/read-status`,
    transitDashboardSearch: `${base.linehaul}/transit-dashboard/search`,
    transitDashboardSearchCount: `${base.linehaul}/transit-dashboard/trips/search-count`,
    transitDashboardShipments: (tripNumber: string | number) =>
      `${base.loadboardV2BaseUrl}/trips/${tripNumber}/shipments`,
    transitDashboardDeliveredTrips: `${base.linehaul}/transit-dashboard/delivered-trips`,
    transitLocationSearch: (searchKey: string) =>
      `${base.linehaul}/transit-dashboard/timezoneoffset?SearchKey=${encodeURIComponent(searchKey)}`,
    getTripsByCarrierInBulkRequest: (carrierCode: string) =>
      `${base.linehaul}/transit-dashboard/trips-by-carrier?CarrierCode=${carrierCode}`,
    sendBulkRequest: `${base.linehaul}/transit-dashboard/bulk-request`,
    getTripNotes: (tripNumber: number) =>
      `${base.linehaul}/transit-dashboard/${tripNumber}/notes`,
    createTripNote: (tripNumber: number) =>
      `${base.linehaul}/transit-dashboard/${tripNumber}/note`,
    updateTripNote: (noteId: number) =>
      `${base.linehaul}/transit-dashboard/note/${noteId}`,
    deleteTripNote: (noteId: number) =>
      `${base.linehaul}/transit-dashboard/notes/${noteId}`,
    starTrip: (tripNumber: number) =>
      `${base.linehaul}/transit-dashboard/${tripNumber}/star`,
    requestStatus: (tripNumber: number) =>
      `${base.linehaul}/transit-dashboard/trip/${tripNumber}/request-status`,
    closedTripDetails: (tripNumber?: number) =>
      `${base.linehaul}/trip/${tripNumber}/closed-details`,
    closeTrip: (tripNumber: number) =>
      `${base.linehaulV2}/loadboard/trip/${tripNumber}/close`,
    editTripClosedDetails: (tripNumber: number) =>
      `${base.linehaulV2}/loadboard/trip/${tripNumber}/update-closed-trip-details`,
    enrouteTrip: (tripNumber: number) =>
      `${base.linehaulV2}/loadboard/trip/${tripNumber}/enroute`,
    changeTripStatus: (tripNumber: number) =>
      `${base.linehaul}/trip/${tripNumber}/change-status`,
    // Note: updateTransitStatus (POST) and editTransitStatus (PATCH) use the same endpoint
    // Kept separate for semantic clarity - one for create, one for edit
    updateTransitStatus: (tripNumber: number) =>
      `${base.linehaul}/transit-dashboard/${tripNumber}/change-status`,
    editTransitStatus: (tripId: number) =>
      `${base.linehaul}/transit-dashboard/${tripId}/change-status`,
    tripDetails: (tripNumber: number) => `${base.linehaul}/trip/${tripNumber}`,
    tripOptimization: (tripNumber: number) =>
      `${base.linehaul}/trip/${tripNumber}/trip-optimization`,
    moveShipments: (tripNumber: number) =>
      `${base.linehaul}/trip/${tripNumber}/trip-optimization/move-shipments`,
    tripsForReassign: `/${base.loadboardV2BaseUrl}/shipments/trips-for-reassign`,
    reassignShipments: `/${base.loadboardV2BaseUrl}/shipments/reassign`,
    unassignShipments: `/${base.loadboardV2BaseUrl}/shipments/unassign`,
    delayReasons: `${base.linehaulV2}/tripList/delay-reasons`,
    ucccNotifications: (date: string) =>
      `${base.linehaulV2}/uc-cc-planning/notifications/${date}`,
    ucccPlanning: (date: string) => `${base.linehaulV2}/uc-cc-planning/${date}`,
    tripsForShipmentDesignation: (sicCode: string) =>
      `${base.linehaul}/trip/trips-for-shipment-designation/${sicCode}`,
    mergeLanes: (originSicCode: string, day: string) =>
      `${base.linehaulV2}/loadboard/${originSicCode}/lane-merge/${day}`,
    unmergeLanes: (originSicCode: string, day: string) =>
      `${base.linehaulV2}/loadboard/${originSicCode}/unmerge-lanes/${day}`,
    designateShipments: `${base.linehaulV2}/uc-cc-planning/trip-designation`,
    trailerInYard: (tripNumber: number) =>
      `${base.linehaulV2}/loadboard/trip/${tripNumber}/trailer-in-yard`,
    bulkManifestShipments: (searchTerm: string) =>
      `${base.linehaul}/bulk-reconcile/shipments/${searchTerm}`,
    tripCBOLInfo: (tripNumber: number) =>
      `${base.linehaul}/trip/${tripNumber}/cbol-info`,
    getTripHistory: (tripNumber: number) =>
      `${base.linehaul}/transit-dashboard/trip/${tripNumber}/status-history`,
    deleteTripHistoryStatus: (tripNumber: number, statusId: number) =>
      `${base.linehaul}/transit-dashboard/${tripNumber}/status/${statusId}`,
    transitDashboardFilterDistinctValues: `${base.linehaul}/transit-dashboard/filters/distinct-values`,
  },

  ucccOfdServiceFailure: {
    getServiceFailureReasonCodes: `${base.linehaul}/uc-cc-ofd/service-failure/reason-codes`,
    updateServiceFailure: (shipmentId: number) =>
      `${base.linehaul}/uc-cc-ofd/${shipmentId}/service-failure`,
    deleteServiceFailure: (shipmentId: number) =>
      `${base.linehaul}/uc-cc-ofd/${shipmentId}/service-failure`,
  },

  // Linehaul - Billing
  linehaulBilling: {
    getFindTrips: `${base.linehaul}/linehaul-billing/find-trip`,
    getFindTripFilterDistinctValues: `${base.linehaul}/linehaul-billing/find-trip/filters/distinct-values`,
    lCodeQueue: `${base.linehaul}/linehaul-billing/l-code-queue`,
    lCodeQueueFilterDistinctValues: `${base.linehaul}/linehaul-billing/l-code-queue/filters/distinct-values`,
    getMCodeQueueZeroOutliersList: () =>
      `${base.linehaul}/linehaul-billing/m-code-outliers`,
    getMCodeQueueStandardAuditQueueList: () =>
      `${base.linehaul}/linehaul-billing/m-code-queue`,
    getTripCostDetails: (tripNumber: number) =>
      `${base.linehaul}/linehaul-billing/trips/${tripNumber}/cost-details`,
    confirmAudit: (tripNumber: number) =>
      `${base.linehaul}/linehaul-billing/trips/${tripNumber}/confirm-audit`,
    lCodeBillingReport: (carrierCode: string) =>
      `${base.linehaul}/linehaul-billing/billing-report/${carrierCode}`,
    employeeQueue: `${base.linehaul}/linehaul-billing/employee-queue`,
    employeeQueueFilterDistinctValues: `${base.linehaul}/linehaul-billing/employee-queue/filters/distinct-values`,
    downloadBillingReport: (carrierCode: string) =>
      `${base.linehaul}/linehaul-billing/billing-report/${encodeURIComponent(carrierCode)}/download`,
  },

  // UC/CC OFD API endpoints
  ucccOfd: {
    ucccOfdDashboardSummary: (date: string) =>
      `${base.linehaul}/uc-cc-ofd/dashboard-summary/${date}`,
    ucccOfdShipments: (date: string, searchTerm?: string) =>
      `${base.linehaul}/uc-cc-ofd/shipments/${date}${searchTerm ? `?searchTerm=${encodeURIComponent(searchTerm)}` : ""}`,
    ucccOfdFilterDistinctValues: (date: string) =>
      `${base.linehaul}/uc-cc-ofd/shipments/${date}/filters/distinct-values`,
    getOwners: () => `${base.linehaul}/uc-cc-ofd/owners`,
    assignOwnerToShipments: `${base.linehaul}/uc-cc-ofd/owner-assignment`,
    serviceFailureRecapReport: (
      serviceStartDate: string,
      serviceEndDate: string,
      includeMonthlySummary: boolean
    ) => {
      const params = new URLSearchParams({
        ServiceStartDate: serviceStartDate,
        ServiceEndDate: serviceEndDate,
        IncludeMonthlySummary: String(includeMonthlySummary),
      });
      return `${base.linehaul}/uc-cc-ofd/service-failure-recap?${params.toString()}`;
    },
    serviceFailureRecapDownloadReport: (
      serviceStartDate: string,
      serviceEndDate: string,
      includeMonthlySummary: boolean,
      documentType: ReportFormatType,
      timezone: string
    ) => {
      const params = new URLSearchParams({
        ServiceStartDate: serviceStartDate,
        ServiceEndDate: serviceEndDate,
        IncludeMonthlySummary: String(includeMonthlySummary),
        DocumentType: documentType,
        TimeZone: timezone,
      });
      return `${base.linehaul}/uc-cc-ofd/service-failure-recap/report?${params.toString()}`;
    },
    updateServiceFailure: (proNumber: string) =>
      `${base.linehaul}/uc-cc-ofd/${proNumber}/service-failure`,
    expedite: (shipmentId: string | number) =>
      `${base.linehaul}/uc-cc-ofd/${shipmentId}/expedite`,
    followUpStatus: (shipmentId: string | number) =>
      `${base.linehaul}/uc-cc-ofd/${shipmentId}/follow-up-status`,
    getUcccOfdShipmentStatusHistory: (shipmentId: string | number) =>
      `${base.linehaul}/uc-cc-ofd/shipment/${shipmentId}/status-history`,
  },

  dispatch: {
    getLatestDriverLocations: (sicId: number, planDate: string) =>
      `${base.dispatch}/v1/latest-drivers-location?sicId=${sicId}&planDate=${planDate}`,
    getDrivers: (
      sicId: number,
      planDate: string,
      searchTerm: string | null | undefined
    ) =>
      `${base.dispatch}/v1/drivers?sicId=${sicId}&planDate=${planDate}${searchTerm ? "&searchTerm=" + encodeURIComponent(searchTerm) : ""}`,
    getRoute: (routeId: number) => `${base.dispatch}/v1/routes/${routeId}`,
    getRoutes: (sicId: number, planDate: string) =>
      `${base.dispatch}/v1/routes?sicId=${sicId}&planDate=${planDate}`,
    getStops: (sicId: number, planDate: string) =>
      `${base.dispatch}/${sicId}/stops?planDate=${planDate}`,
    getStopsV2: (sicId: number, planDate: string) =>
      `${base.dispatch}/v2/${sicId}/stops?planDate=${planDate}`,
    getStop: (sicId: number, id: number) =>
      `${base.dispatch}/${sicId}/stops/${id}`,
    getStopLocationPhotos: (sicId: number, id: number) =>
      `${base.dispatch}/${sicId}/stops/${id}/misc`,
    getSummary: (sicId: number, planDate: string) =>
      `${base.dispatch}/${sicId}/summary?planDate=${planDate}`,
    getSummaryV2: (sicId: number, planDate: string) =>
      `${base.dispatch}/v2/${sicId}/summary?planDate=${planDate}`,
    putStopAddress: (sicId: number, id: number) =>
      `${base.dispatch}/${sicId}/stops/${id}/address`,
    postUnassignStopFromRoute: (sicId: number, id: number) =>
      `${base.dispatch}/${sicId}/stops/${id}/unassign`,
    getSuggestedRoutes: (sicId: number, stopId: number, planDate: string) =>
      `${base.dispatch}/${sicId}/suggest-routes/${stopId}?planDate=${planDate}`,
    postAssignStopToRouteV2: (sicId: number, id: number) =>
      `${base.dispatch}/v2/${sicId}/stops/${id}/assign`,
    postMergeStopV2: (sicId: number, targetStopId: number) =>
      `${base.dispatch}/v2/${sicId}/stops/${targetStopId}/merge`,
    postMultiAssignStopsToRouteV2: (sicId: number) =>
      `${base.dispatch}/v2/${sicId}/stops/multi-assign`,
    postReassignStopToRouteV2: (sicId: number, id: number) =>
      `${base.dispatch}/v2/${sicId}/stops/${id}/reassign`,
    postSplitStop: (sicId: number, id: number) =>
      `${base.dispatch}/${sicId}/stops/${id}/split`,
    getStopShipmentNotes: (sicId: number, shipmentId: number) =>
      `${base.dispatch}/${sicId}/shipment/${shipmentId}/notes`,
    patchStopSic: (sicId: number, id: number) =>
      `${base.dispatch}/${sicId}/stops/${id}/sic`,
    getStopSuggestedSics: (sicId: number, id: number) =>
      `${base.dispatch}/${sicId}/stops/${id}/suggested-sics`,
    getStopShipmentPhotos: (sicId: number, shipmentId: number) =>
      `${base.dispatch}/${sicId}/shipment/${shipmentId}/misc`,
    postUpdateRouteStatus: (sicId: number, id: number) =>
      `${base.dispatch}/${sicId}/routes/${id}/status`,
    getAvailableRouteStatuses: (sicId: number, routeId: number) =>
      `${base.dispatch}/${sicId}/routes/${routeId}/available-statuses`,
    postRescheduleStop: (sicId: number, stopId: number) =>
      `${base.dispatch}/${sicId}/stops/${stopId}/reschedule`,
    getNotifications: (sicId: number, planDate: string) =>
      `${base.dispatch}/v1/notifications?sicId=${sicId}&planDate=${planDate}`,
    postMarkNotificationsAsRead: () =>
      `${base.dispatch}/v1/notifications/mark-as-read`,
    getShipments: (
      sicId: number,
      planDate: string,
      searchTerm: string | null | undefined
    ) =>
      `${base.dispatch}/v1/shipments?sicId=${sicId}&planDate=${planDate}${searchTerm ? "&searchTerm=" + encodeURIComponent(searchTerm) : ""}`,
    getShipmentDetails: (shipmentId: number) =>
      `${base.dispatch}/v1/shipments/${shipmentId}`,
    getShipmentNotes: (shipmentId: number) =>
      `${base.dispatch}/v1/shipments/${shipmentId}/notes`,
    getShipmentHistory: (shipmentId: number) =>
      `${base.dispatch}/v1/shipments/${shipmentId}/history`,
    getShipmentDocuments: (shipmentId: number) =>
      `${base.dispatch}/v1/shipments/${shipmentId}/documents`,
    getSuggestedStops: (sicId: number, routeId: number, planDate: string) =>
      `${base.dispatch}/${sicId}/suggest-stops/${routeId}?planDate=${planDate}`,
    postMarkStopAsIncomplete: (sicId: number, stopId: number) =>
      `${base.dispatch}/${sicId}/stops/${stopId}/mark-as-incomplete`,
    postCompleteStop: (sicId: number, stopId: number) =>
      `${base.dispatch}/${sicId}/stops/${stopId}/complete`,
    getStopExceptionReasons: (sicId: number) =>
      `${base.dispatch}/${sicId}/stops/exception-reasons`,
    getReasons: () => `${base.dispatch}/v1/reasons`,
    patchReorderStops: (routeId: number) =>
      `${base.dispatch}/v1/routes/${routeId}/reorder-stops`,
    isZipCodeServiceable: (
      destinationZipCode: string,
      destinationCountryCode: string
    ) =>
      `${base.dispatch}/v1/zipcodes?DestinationZipCode=${destinationZipCode}&DestinationCountryCode=${destinationCountryCode}`,
  },

  ratesAndAccessorial: {
    getLCodes: () => `${base.linehaul}/settings/rates-accessorials/l-codes`,
    getMCodeVendors: () =>
      `${base.linehaul}/settings/rates-accessorials/m-code/vendors`,
    getMCodeVendorList: (rateId?: number) =>
      `${base.linehaul}/settings/rates-accessorials/owner-operator/vendor-assignments${rateId ? `?rateId=${rateId}` : ""}`,
    addNewMCodeVendor: (rateId: number) =>
      `${base.linehaul}/settings/rates-accessorials/owner-operator/rates/${rateId}/vendor-assignments`,
    getLCodesCarrierFilterOptions: `${base.linehaul}/settings/rates-accessorials/filters/distinct-values`,
    getTolls: `${base.linehaul}/settings/rates-accessorials/tolls`,
    editToll: (tollId: number) =>
      `${base.linehaul}/settings/rates-accessorials/tolls/${tollId}/edit`,
    getTollsFilterDistinctValues: `${base.linehaul}/settings/rates-accessorials/tolls/filters/distinct-values`,
    expireRate: (rateId: number) =>
      `${base.linehaul}/settings/rates-accessorials/rates/${rateId}/expire`,
    expireMRate: (rateId: number) =>
      `${base.linehaul}/settings/rates-accessorials/owner-operator/rates/${rateId}/expire`,
    expireAndDuplicateRate: (rateId: number) =>
      `${base.linehaul}/settings/rates-accessorials/rates/${rateId}/expire-duplicate`,
    expireAndDuplicateMRate: (rateId: number) =>
      `${base.linehaul}/settings/rates-accessorials/owner-operator/rates/${rateId}/expire-duplicate`,
    getLCodesExpiredAccessorials: (carrierCode: string) =>
      `${base.linehaul}/settings/rates-accessorials/carrier/${carrierCode}/expired-accessorials`,
    getLCodesExpiredRates: (carrierCode: string) =>
      `${base.linehaul}/settings/rates-accessorials/carrier/${carrierCode}/expired-rates`,
    getMCodesExpiredAccessorials: (): string =>
      `${base.linehaul}/settings/rates-accessorials/owner-operator/expired-accessorials`,
    getMCodesAccessorials: () =>
      `${base.linehaul}/settings/rates-accessorials/owner-operator/accessorials`,
    updateMCodesAccessorials: () =>
      `${base.linehaul}/settings/rates-accessorials/owner-operator/accessorials`,
    createMCodeRate: () =>
      `${base.linehaul}/settings/rates-accessorials/owner-operator/rates`,
    getMCodeLanes: () =>
      `${base.linehaul}/settings/rates-accessorials/owner-operator/lanes`,
    getMCodesExpiredRates: () =>
      `${base.linehaul}/settings/rates-accessorials/owner-operator/expired-rates`,
    downloadAllLCodeRates: `${base.linehaul}/rates/l-codes/download`,
    importLCodeRates: (carrierCode: string) =>
      `${base.linehaul}/rates/l-codes/${carrierCode}/import`,
    validateImportLCodeRates: (carrierCode: string) =>
      `${base.linehaul}/rates/l-codes/${carrierCode}/validate-import`,
    downloadAllMCodeRates: `${base.linehaul}/rates/owner-operator/download`,
    getMCodesExpiredTolls: () =>
      `${base.linehaul}/settings/rates-accessorials/tolls/expired`,
    getMRates: () =>
      `${base.linehaul}/settings/rates-accessorials/owner-operator/rates`,
    deleteMCodeRate: (rateId: number) =>
      `${base.linehaul}/settings/rates-accessorials/owner-operator/rates/${rateId}`,
    editMCodeLanes: (rateId: number) =>
      `${base.linehaul}/settings/rates-accessorials/owner-operator/${rateId}/lanes`,
    editMCodeRate: (rateId: number) =>
      `${base.linehaul}/settings/rates-accessorials/owner-operator/rates/${rateId}`,
  },

  lhJunctionSetup: {
    getLhJunctionSetupContacts: (sicCode: string) =>
      `${base.linehaul}/junction-setup/${sicCode}/contacts`,
    deleteLhJunctionSetupContact: (contactId: number) =>
      `${base.linehaul}/junction-setup/contacts/${contactId}`,
    createLhJunctionSetupContact: (sicCode: string) =>
      `${base.linehaul}/junction-setup/${sicCode}/contacts`,
    updateLhJunctionSetupContact: (contactId: number) =>
      `${base.linehaul}/junction-setup/contacts/${contactId}`,
    getLhJunctionSetupFormValues: (sicCode: string) =>
      `${base.linehaul}/junction-setup/${sicCode}/configuration`,
    updateLhJunctionSetupConfiguration: (sicCode: string) =>
      `${base.linehaul}/junction-setup/${sicCode}/configuration`,
    getExpiredIbGateTimes: `${base.linehaul}/junction-setup/ib-gate-times/expired`,
    updateIbGateTime: (id: number) =>
      `${base.linehaul}/junction-setup/ib-gate-times/${id}`,
    updateObGateTime: (id: number) =>
      `${base.linehaul}/junction-setup/ob-gate-times/${id}`,
    getExpiredObGateTimes: (laneId: number) =>
      `${base.linehaul}/junction-setup/ob-gate-times/expired/${laneId}`,
    getObGateTimeSuggestion: `${base.linehaul}/junction-setup/ob-gate-times`,
    expireLhJunctionSetupException: `${base.linehaul}/junction-setup/exceptions/expire`,
  },

  getPresetLanes: (sicCode: string) =>
    `operations/outbound/${sicCode}/PresetLane`,
  getTrailerTypes: () => "operations/outbound/TrailerType",
  getPresetLaneSicSuggestion: (sicCode: string, destinationSic: number) =>
    `${base.outbound}/${sicCode}/PresetLane/sics-suggestion?laneId=${destinationSic}`,
  getPresetLaneSics: (sicCode: string) =>
    `${base.outbound}/${sicCode}/PresetLane/lanes`,

  // Outbound
  createOrUpdatePresetLanes: (sicCode: string) =>
    `${base.outbound}/${sicCode}/PresetLane`,
  getOrDeletePresetLane: (sicCode: string, laneId: number) =>
    `${base.outbound}/${sicCode}/PresetLane/${laneId}`,
  getLanes: (sicCode: string) => `operations/outbound/${sicCode}/Lane/`,
  createOrUpdateStaticLoadTemplate: (sicCode: string) =>
    `${base.outbound}/${sicCode}/StaticLoadTemplate`,
  createOrUpdateStaticLoadTemplateAssets: (sicCode: string) =>
    `${base.outbound}/${sicCode}/StaticLoadTemplate/assets`,
  getOrDeleteStaticLoadTemplate: (sicCode: string, laneId: number) =>
    `${base.outbound}/${sicCode}/StaticLoadTemplate/${laneId}`,
  getStaticLoadTemplates: (sicCode: string) =>
    `${base.outbound}/${sicCode}/StaticLoadTemplate/`,
  duplicateStaticLoadTemplate: (sicCode: string, templateId: number) =>
    `${base.outbound}/${sicCode}/StaticLoadTemplate/${templateId}/Duplicate`,
  getDefaultLoadTemplates: (sicCode: string) =>
    `${base.outbound}/${sicCode}/DefaultTemplate/`,
  defaultStaticLoadTemplate: (sicCode: string, templateId: number) =>
    `${base.outbound}/${sicCode}/DefaultTemplate/${templateId}`,
  updateDefaultLoadTemplate: (sicCode: string) =>
    `${base.outbound}/${sicCode}/DefaultTemplate`,
  getDoorTypes: `${base.outbound}/DoorType`,
  getDoorLayouts: (sicCode: string) =>
    `${base.outbound}/${sicCode}/layout/doors`,
  getSectors: (sicCode: string) => `${base.outbound}/${sicCode}/layout/sectors`,
  getDockLayout: (sicCode: string) => `${base.outbound}/${sicCode}/layout`,
  getLaneDetailsTonnage: (sicCode: string, sectors: string[]) =>
    `${base.outbound}/${sicCode}/Freight/v3/tonnage${
      sectors.length > 0 ? `?sectors=${sectors.join(",")}` : ""
    }`,
  getFreightDetailsByDoor: (sicCode: string, sectors: string[]) =>
    `${base.outbound}/${sicCode}/Freight/v2/tonnage-by-door${
      sectors.length > 0 ? `?sectors=${sectors.join(",")}` : ""
    }`,
  getDestinationShipmentsByLane: (
    sicCode: string,
    destinationType: number,
    loadToSicId: number,
    sicId: number
  ) =>
    `${base.outbound}/${sicCode}/Freight/${destinationType}/lanes/${loadToSicId}/destinations/${sicId}/shipments`,
  getDoorShipments: (sicCode: string, doorNumber: number) =>
    `${base.outbound}/${sicCode}/Freight/v2/doors/${doorNumber}/shipments`,
  getDoorShipmentsByLane: (
    sicCode: string,
    laneType: LaneType,
    destinationSicId: number,
    doorNumber: number
  ) =>
    `${base.outbound}/${sicCode}/Freight/v2/lanes/${laneType}/${destinationSicId}/doors/${doorNumber}/shipments`,
  moveShipmentsToDoor: (sicCode: string, doorNumber: number) =>
    `${base.outbound}/${sicCode}/Shipment/move-shipments-to-door/${doorNumber}`,
  moveShipmentsToDoorByLane: (
    sicCode: string,
    laneType: LaneType,
    destinationSicId: number,
    doorNumber: number
  ) =>
    `${base.outbound}/${sicCode}/Shipment/v2/lanes/${laneType}/${destinationSicId}/doors/${doorNumber}/move-shipments-to-door`,
  moveShipmentsToLane: (sicCode: string, laneType: number, laneSicId: number) =>
    `${base.outbound}/${sicCode}/Shipment/v2/divert-shipments/${laneType}/${laneSicId}`,
  moveShipmentsToTrap: (sicCode: string, trapId: number) =>
    `${base.outbound}/${sicCode}/Shipment/move-shipments-to-trap/${trapId}`,
  sicsSuggestion: (sicCode: string, destinationSicId: number) =>
    `${base.outbound}/${sicCode}/load-plan/lanes/${destinationSicId}/sics-suggestion`,
  splitLane: (sicCode: string, destinationSicId: number) =>
    `${base.outbound}/${sicCode}/load-plan/lanes/${destinationSicId}/split-lane`,
  currentPlanDate: (sicCode: string) =>
    `${base.outbound}/${sicCode}/load-plan/current-plan-date`,
  editAssignAsset: (sicCode: string, plannedPullId: number) =>
    `${base.outbound}/${sicCode}/load-plan/planned-pulls/${plannedPullId}/v2/associate-linehaul-assets`,
  editAssignTrap: (sicCode: string, plannedPullId: number) =>
    `${base.outbound}/${sicCode}/load-plan/planned-pulls/${plannedPullId}/v2/associate-trap-assets`,
  removeAsset: (sicCode: string, plannedPullId: number) =>
    `${base.outbound}/${sicCode}/load-plan/planned-pulls/${plannedPullId}`,
  assignAsset: (sicCode: string, doorNumber: number) =>
    `${base.outbound}/${sicCode}/load-plan/doors/${doorNumber}/associate-linehaul-assets`,
  assignTrap: (sicCode: string, doorNumber: number) =>
    `${base.outbound}/${sicCode}/load-plan/doors/${doorNumber}/associate-trap-assets`,
  editDoorAndAssetsFromLaneOld: (sicCode: string, doorNumber: number) =>
    `${base.outbound}/${sicCode}/load-plan/doors/${doorNumber}/assets`,
  editDoorAndAssetsFromLane: (sicCode: string, doorNumber: number) =>
    `${base.outbound}/${sicCode}/load-plan/V2/doors/${doorNumber}/assets`,
  unassignDoorFromLane: (sicCode: string, laneId: number, doorNumber: number) =>
    `${base.outbound}/${sicCode}/load-plan/lanes/${laneId}/unassign-door/${doorNumber}`,
  getEstimateShipmentTransit: (
    sicCode: string,
    laneSicCode: string,
    shipmentIds: Array<number>
  ) =>
    `${
      base.outbound
    }/${sicCode}/transit-days/${laneSicCode}/estimate-shipment-transit?ShipmentIds=${shipmentIds.join(
      ","
    )}`,
  getAlternateLanes: (
    sicCode: string,
    laneSicCode: string,
    alternateLaneSicCode: string
  ) =>
    `${base.outbound}/${sicCode}/transit-days/${laneSicCode}/recommend-alternate-lanes/${alternateLaneSicCode}`,
  getOutboundShipmentDetails: (sicCode: string, shipment: number) =>
    `${base.outbound}/${sicCode}/Shipment/${shipment}/Details`,
  getOutboundShipmentDetailsV2: (
    sicCode: string,
    laneType: LaneType,
    destinationSicId: number
  ) =>
    `${base.outbound}/${sicCode}/freight/v2/lanes/${laneType}/${destinationSicId}/shipments`,
  getLaneDetails: (sicCode: string, laneId: number) =>
    `${base.outbound}/${sicCode}/load-plan/lanes/${laneId}`,
  getDoorAssets: (
    sicCode: string,
    laneType: LaneType,
    destinationSicId: number
  ) =>
    `${base.outbound}/${sicCode}/load-plan/v4/lanes/${destinationSicId}/type/${laneType}`,
  getOrEditDoorDetails: (sicCode: string, doorNumber: number) =>
    `${base.outbound}/${sicCode}/load-plan/doors/${doorNumber}`,
  reassignDoorDetails: (sicCode: string, doorNumber: number) =>
    `${base.outbound}/${sicCode}/load-plan/door/${doorNumber}/reassign`,
  getTrapDetails: (sicCode: string) =>
    `${base.outbound}/${sicCode}/load-plan/trap-details`,
  mergeLanes: (sicCode: string, destinationSicId: number) =>
    `${base.outbound}/${sicCode}/load-plan/lanes/${destinationSicId}/merged-lanes`,
  mergeDoors: (sicCode: string, destinationSicId: number) =>
    `${base.outbound}/${sicCode}/load-plan/lanes/${destinationSicId}/merge-doors`,
  mergeDoorsAssets: (sicCode: string, destinationSicId: number) =>
    `${base.outbound}/${sicCode}/load-plan/lanes/${destinationSicId}/merge-door-assets`,
  getDoorsUsageInfo: (sicCode: string) =>
    `${base.outbound}/${sicCode}/load-plan/doors-usage-information`,
  shipmentTags: `${base.outbound}/ShipmentTags`,
  getLinehaulTrips: (sicCode: string, destinationSicCode: string) =>
    `${base.outbound}/${sicCode}/load-plan/linehaul-trips/${destinationSicCode}`,
  getEquipments: (sicCode: string) => `${base.outbound}/${sicCode}/equipment`,
  getOutboundCarriers: `${base.outbound}/carrier`,
  getMultipleShipmentDetails: (
    sicCode: string,
    shipmentIds: Array<number>,
    laneId: number
  ) =>
    `${base.outbound}/${sicCode}/Shipment/?ShipmentIds=${shipmentIds.join(
      ","
    )}&LaneId=${laneId}`,
  getMultipleLanesDetails: (sicCode: string, lanesIds: Array<number>) =>
    `${base.outbound}/${sicCode}/load-plan/lanes?laneIds=${lanesIds.join(",")}`,
  getMultipleDoorDetails: (sicCode: string, doorNumbers: Array<number>) =>
    `${
      base.outbound
    }/${sicCode}/load-plan/doors-details?doors=${doorNumbers.join(",")}`,
  getUrgentAndCriticalCare: (sicCode: string) =>
    `${base.outbound}/${sicCode}/urgent-care-critical-care/`,
  getOutboundLinehaulTrailers: ({
    sicCode,
    eta,
    isPlanned,
  }: {
    sicCode: string;
    eta: string;
    isPlanned?: boolean;
  }) =>
    `${base.outbound}/${sicCode}/trailers-hub/linehaul?eta=${eta}${
      isPlanned !== undefined ? `&planned=${isPlanned}` : ""
    }`,
  getOutboundLoadTrailersOld: (sicCode: string) =>
    `${base.outbound}/${sicCode}/trailers-hub/load`,
  getOutboundLoadTrailers: (sicCode: string) =>
    `${base.outbound}/${sicCode}/trailers-hub/v2/load`,
  changeTrailerVisibility: (sicCode: string) =>
    `${base.outbound}/${sicCode}/trailers-hub/loads/visibility`,
  swingTrailer: (sicCode: string, plannedPullId: number) =>
    `${base.outbound}/${sicCode}/trailers-hub/loads/${plannedPullId}/swing`,
  changeTripForLoad: (sicCode: string, plannedPullId: number) =>
    `${base.outbound}/${sicCode}/trailers-hub/load/${plannedPullId}/trip`,
  changeStatusOutboundLoads: (
    sicCode: string,
    plannedPullId: number,
    newStatus: string
  ) =>
    `${base.outbound}/${sicCode}/trailers-hub/load/${plannedPullId}/status/${newStatus}`,
  getPndTrailers: (sicCode: string) =>
    `${base.outbound}/${sicCode}/trailers-hub/pickup-delivery/`,
  getTrapTrailers: (sicCode: string) =>
    `${base.outbound}/${sicCode}/trailers-hub/trap/`,
  transformPndToTrap: (sicCode: string) =>
    `${base.outbound}/${sicCode}/trailers-hub/pickup-delivery/transform-to-trap`,
  getOutboundLinehaulTrailerShipments: (sicCode: string, tripNumber: number) =>
    `${base.outbound}/${sicCode}/trailers-hub/linehaul/${tripNumber}/shipments`,
  getStopOffShipments: (sicCode: string, tripNumber: number) =>
    `${base.outbound}/${sicCode}/trailers-hub/linehaul/${tripNumber}/two-stop-off-shipments`,
  processStopOff: (sicCode: string, tripNumber: number) =>
    `${base.outbound}/${sicCode}/trailers-hub/linehaul/${tripNumber}/process-stop-off`,
  getOutboundLoadTrailerShipments: (sicCode: string, plannedPullId: number) =>
    `${base.outbound}/${sicCode}/trailers-hub/load/${plannedPullId}/shipments`,
  getFreightOnDock: (sicCode: string) =>
    `${base.outbound}/${sicCode}/trailers-hub/freight-on-dock/`,
  getOutboundPndTrailerShipments: (sicCode: string, tripNumber: number) =>
    `${base.outbound}/${sicCode}/trailers-hub/pickup-delivery/${tripNumber}/shipments`,
  getOutboundTrapTrailerShipments: (sicCode: string, trapId: number) =>
    `${base.outbound}/${sicCode}/trailers-hub/trap/${trapId}/shipments`,
  addLinehaulTrailersToPlan: (sicCode: string) =>
    `${base.outbound}/${sicCode}/load-plan/linehaul-trips/add`,
  removeLinehaulTrailersFromPlan: (sicCode: string) =>
    `${base.outbound}/${sicCode}/load-plan/linehaul-trips/remove`,
  reconcileShipmentsOutboundLoads: (sicCode: string, plannedPullId: number) =>
    `${base.outbound}/${sicCode}/trailers-hub/load/${plannedPullId}/add-shipments`,
  removeShipmentsOutboundLoads: (sicCode: string, plannedPullId: number) =>
    `${base.outbound}/${sicCode}/trailers-hub/load/${plannedPullId}/remove-shipments`,
  getOutboundShipmentsSearch: (
    sicCode: string,
    plannedPullId: number,
    proNumber: string
  ) =>
    `${base.outbound}/${sicCode}/transit-days/pulls/${plannedPullId}/estimate-shipment-transit/${proNumber}`,
  getOutboundLinehaulTrailerShipmentsPdf: (
    sicCode: string,
    tripNumbers: Array<number>
  ) =>
    `${base.outbound}/${sicCode}/trailers-hub/linehaul/unload-manifest?tripNumbers=${tripNumbers.join(",")}&returnType=json`,
  getOutboundPndTrailerShipmentsPdf: (
    sicCode: string,
    tripNumbers: Array<number>
  ) =>
    `${base.outbound}/${sicCode}/trailers-hub/pickup-delivery/strip-manifest?tripNumbers=${tripNumbers.join(",")}&returnType=json`,
  getOutboundTrapTrailerShipmentsPdf: (
    sicCode: string,
    trapIds: Array<number>
  ) =>
    `${base.outbound}/${sicCode}/trailers-hub/trap/unload-manifest?trapIds=${trapIds.join(",")}&returnType=json`,
  getOutboundLoadTrailerShipmentsPdf: (
    sicCode: string,
    tripNumbers: Array<number>
  ) =>
    `${base.outbound}/${sicCode}/trailers-hub/load/carrier-bill-of-lading?tripNumbers=${tripNumbers.join(",")}&returnType=json`,
  getLoadTrailerNotes: (sicCode: string, plannedPullId: number) =>
    `${base.outbound}/${sicCode}/trailers-hub/load/${plannedPullId}/notes`,
  createLoadTrailerNote: (sicCode: string, plannedPullId: number) =>
    `${base.outbound}/${sicCode}/trailers-hub/load/${plannedPullId}/notes`,
  updateLoadTrailerNote: (sicCode: string, plannedPullId: number) =>
    `${base.outbound}/${sicCode}/trailers-hub/load/${plannedPullId}/notes/`,
  deleteLoadTrailerNote: (
    sicCode: string,
    plannedPullId: number,
    noteId: number
  ) =>
    `${base.outbound}/${sicCode}/trailers-hub/load/${plannedPullId}/notes/${noteId}`,
  common: {
    getOsadDetails: (sicId: number) =>
      `${base.common}/service-centers/${sicId}/overage-shortage-damage/shipments`,
    getReasonCodes: `${base.common}/shipments/overage-shortage-damage/reason-codes`,
    createOSAD: (sicId: number) =>
      `${base.common}/service-centers/${sicId}/overage-shortage-damage`,
  },

  // Trip
  getTripShuttle: "operations/trip/base-trips-shuttle-v2",
  getTripLH: "operations/trip/base-trips-lh-v2",
  getTripLHStops: "operations/trip/stops",

  // Lane
  editLane: (laneId: string) => `${base.lane}/lanes/${laneId}`,
  createLoadTemplate: `${base.lane}/load-templates`,
  getLoadTemplate: (id: string) => `${base.lane}/load-templates/${id}`,
  getLoadTemplates: `${base.lane}/load-templates`,
  editLoadTemplate: (templateId: string) =>
    `${base.lane}/load-templates/${templateId}`,

  // Service Center
  getServiceCenterLayoutByCode: (serviceCenterCode: string) =>
    `${base.serviceCenter}/service-center-layouts/${serviceCenterCode}`,
  getServiceCenterByCode: (code: string) =>
    `${base.serviceCenter}/service-center/${code}`,
  editServiceCenter: (sicCode: string) =>
    `${base.serviceCenter}/service-center/${sicCode}`,
  getServiceCenters: `${base.serviceCenter}/service-centers`,
  putServiceCenterLayout: `${base.serviceCenter}/service-center-layouts`,
  getDoorNumbersBySicIdOld: (sidId: number) =>
    `${base.serviceCenter}/GetDoorNumbersBySicId/${sidId}`,
  getDoorNumbersBySicId: (sidId: number) =>
    `${base.inboundPlanning}/service-center/${sidId}/doors-usage-info`,
  getRegionsWithSics: `${base.linehaul}/region/service-centers`,
  createRegion: `${base.linehaul}/region`,
  updateRegion: (id: number) => `${base.linehaul}/region/${id}`,
  deleteRegion: (id: number) => `${base.linehaul}/region/${id}`,
  getSics: `${base.linehaul}/lh-service-centers`,

  getRegionById: (id: number) => `${base.linehaulV2}/region/${id}`,
  getRegionsCount: `${base.linehaulV2}/region/count`,
  getRegionsWithSicsV2: `${base.linehaulV2}/region/service-centers`,
  createRegionV2: `${base.linehaulV2}/region`,
  updateRegionV2: (id: number) => `${base.linehaulV2}/region/${id}`,
  deleteRegionV2: (id: number) => `${base.linehaulV2}/region/${id}`,
  updateRegionName: (id: number) => `${base.linehaulV2}/region/${id}/name`,

  getSicsWithRegion: `${base.linehaul}/service-center`,
  getSicByCode: (code: string) => `${base.linehaul}/lh-service-center/${code}`,
  getContactBySicCodeAndId: (code: string, contactId?: number) =>
    `${base.linehaul}/lh-service-center/${code}/contact/${contactId}`,
  addSicContact: (code: string) =>
    `${base.linehaul}/lh-service-center/${code}/contact`,
  unassignSic: (code: string) =>
    `${base.linehaul}/lh-service-center/${code}/unassign`,
  unassignSicV2: (regionId: number, code: string) =>
    `${base.linehaulV2}/region/${regionId}/service-centers/${code}/unassign`,

  // Biometrics (DockAuto) — lists employees + independent contractors
  getBiometricUsers: (pageIndex = 0, pageSize = 1000) =>
    `${base.dockAuto}/biometrics?pageIndex=${pageIndex}&pageSize=${pageSize}`,
  getBiometricUserById: (id: string) => `${base.dockAuto}/biometrics/${id}`,

  // Biometrics (Access Control) —
  getBiometricUsersAccessControl: (pageIndex = 0, pageSize = 1000) =>
    `${base.accessControl}/biometrics?pageIndex=${pageIndex}&pageSize=${pageSize}`,
  getBiometricUserByIdAccessControl: (id: string) =>
    `${base.accessControl}/biometrics/${id}`,
  resetFaceId: `${base.accessControl}/biometrics/sign-up/reset`,

  // Status
  getStatuses: "status-api/status",

  // Split IO API
  getMySegments: (userId: string) => `${base.splitIoApi}/mySegments/${userId}`,

  // Reports
  getBulkReport: `${base.reports}/bulk`,
  getServiceCentersForLoadPlanReport: (date: string) =>
    `${base.linehaul}/load-plan-reports-service-centers/by-date/${date}`,
  getLoadPlanReports: (sicId: number, date: string) =>
    `${base.linehaul}/service-centers/${sicId}/load-plan-reports/vendor-compliance?date=${date}`,
  getLoadAndHoldReports: (sicId: number, date: string) =>
    `${base.linehaul}/service-centers/${sicId}/load-plan-reports/load-and-hold?date=${date}`,
  getTripsReports: (sicId: number, date: string) =>
    `${base.linehaul}/service-centers/${sicId}/load-plan-reports/trip?date=${date}`,
  updateLoadPlanReportNotes: (originSic: number, tripNumber: number) =>
    `${base.linehaul}/service-centers/${originSic}/load-plan-reports/${tripNumber}/note`,
  getUrgentCriticalCareReports: (sicId: number, date: string) =>
    `${base.linehaul}/service-centers/${sicId}/load-plan-reports/urgent-critical-care?date=${date}`,
  getVendorComplianceReports: (sicId: number, date: string) =>
    `${base.linehaul}/service-centers/${sicId}/load-plan-reports/vendor-compliance?date=${date}`,
  getLoadPlanReportsPdf: (
    sicId: number,
    action: string,
    date: string,
    reportType: string,
    generatedTimestamp: string,
    timezone: string
  ) =>
    `${base.linehaul}/service-centers/${sicId}/load-plan-reports/pdf?action=${action}&date=${date}&reportType=${reportType}&generatedTimestamp=${generatedTimestamp}&timezone=${encodeURIComponent(timezone)}`,
  getOutboundTrapManifestReport: `${base.reports}/unload/outbound/traps`,
  getOutboundLinehaulTrailerManifestReport: `${base.reports}/line-haul-reports/outbound/unload-manifest`,
  printStopOffUnloadManifest: `${base.reports}/line-haul-reports/outbound/unload-shipments-manifest`,
  getOutboundStripManifestReport: `${base.reports}/strip-manifest`,
  getOutboundCarrierBillOfLadingManifestReport: `${base.reports}/carrier-bill-of-lading`,
  getCancellationReport: `${base.linehaul}/service-centers/cancellations-report`,
  getCancellationReportPdf: `${base.linehaul}/service-centers/cancellations-report/pdf`,
  getCancellationReportCsv: `${base.linehaul}/service-centers/cancellations-report/csv`,

  getEndOfShiftReports: (
    sicId: number | null,
    date: string,
    reportView: string
  ) =>
    `${base.linehaul}/service-centers/${sicId}/end-of-shift-reports/${date}/${reportView}`,
  deleteYardCheckFile: (sicId: number | null, fileId: string) =>
    `${base.linehaul}/service-centers/${sicId}/end-of-shift-reports/yard-check/${fileId}`,
  getEndOfShiftReportDownload: (sicId: number | null, fileUri: string) =>
    `${base.linehaul}/service-centers/${sicId}/end-of-shift-reports/download-yard-check-file?fileUri=${fileUri}`,

  // End of Shift Comments
  postEndOfShiftComment: `${base.linehaul}/comments`,
  getEndOfShiftComment: (id: string) => `${base.linehaul}/comments/${id}`,
  getEndOfShiftComments: `${base.linehaul}/comments`,
  deleteEndOfShiftComment: (id: string) => `${base.linehaul}/comments/${id}`,
  editEndOfShiftComment: (id: string) => `${base.linehaul}/comments/${id}`,
  resolveEndOfShiftComment: (id: string) =>
    `${base.linehaul}/comments/${id}/resolve`,
  unresolveEndOfShiftComment: (id: string) =>
    `${base.linehaul}/comments/${id}/unresolve`,

  // End of Shift Latest Modifications
  getEndOfShiftLatestModifications: `${base.linehaul}/comments/latest-modification`,

  // BackOffice
  getBackOfficeAccounts: (searchTerm: string) =>
    `${base.backOffice}/accounts/${searchTerm}`,

  getClientAccountDetails: `${base.backOffice}/accounts`,
  getSearchAccountsPreferences: (searchQuery: string) =>
    `${base.backOffice}/accounts/preferences/search?search_query=${encodeURIComponent(searchQuery)}`,
  getRecentAccountsPreferences: `${base.backOffice}/accounts/preferences/recent`,
  saveAccountPreferences: `${base.backOffice}/accounts/preferences/save`,
  getZipCodes: (zipcode: string) => `${base.backOffice}/zip-codes/${zipcode}`,
  getAdditionalServices: `${base.backOffice}/instruction-codes/additional-services`,
  getShippingInstructions: `${base.backOffice}/instruction-codes/shipping-instructions`,
  createQuickQuote: `${base.backOffice}/quick-quote/create`,
  updateQuickQuote: (guaranteedServiceId: number) =>
    `${base.backOffice}/quick-quote/update?guaranteedServiceId=${guaranteedServiceId}`,
  getQuickQuoteDetails: (guaranteedServiceId: number) =>
    `${base.backOffice}/quick-quote/${guaranteedServiceId}`,
  getShipments: `${base.backOffice}/shipments`,
  getShipmentsStatus: `${base.backOffice}/shipments/status`,
  exportShipments: `${base.backOffice}/shipments/export`,
  getServiceLevelOptions: `${base.backOffice}/fuel/scale/service-level`,
  getFuelScales: `${base.backOffice}/fuel/scale`,
  getBillingQueues: `${base.backOffice}/billing-queue`,
  getBillingQueuesFilters: `${base.backOffice}/billing-queue/filters`,
  getFuelScaleAccounts: `${base.backOffice}/fuel/scale/accounts`,
  getFuelScaleAccountCount: (fuelCode: string) =>
    `${base.backOffice}/fuel/scale/account-count/${fuelCode}`,
  getLaneMinimumSchedules: `${base.backOffice}/lane-minimum-schedules`,
  getLaneMinimumScheduleById: (scheduleId: number | string) =>
    `${base.backOffice}/lane-minimum-schedules/${scheduleId}`,
  getLaneMinimums: `${base.backOffice}/lane-minimums`,
  getLaneMinimumLocationTypes: `${base.backOffice}/lane-minimums/location-types`,
  getLaneMinimumLocationValues: `${base.backOffice}/lane-minimums/location-values`,

  // Transit Times & Service Levels - Service Holidays
  getServiceHolidaysList: `${base.backOffice}/service-holidays`,
  getServiceHolidaysCarriers: `${base.backOffice}/service-holidays/carriers`,
  postServiceHolidaysEolCentersByCarriers: `${base.backOffice}/service-holidays/eol-centers/by-carriers`,
  postServiceHolidays: `${base.backOffice}/service-holidays/create`,
  getServiceHolidayDetails: (id: string, search?: string) =>
    search?.trim()
      ? `${base.backOffice}/service-holidays/${id}?search=${encodeURIComponent(search.trim())}`
      : `${base.backOffice}/service-holidays/${id}`,
  putServiceHoliday: (id: string) =>
    `${base.backOffice}/service-holidays/${id}`,
  deleteServiceHoliday: (id: string) =>
    `${base.backOffice}/service-holidays/${id}`,
  fetchAndCheckServiceHolidays: `${base.backOffice}/service-holidays/fetch-and-check`,
  bulkImportServiceHolidays: `${base.backOffice}/service-holidays/bulk-import`,
  // Transit Times & Service Levels - Global Holidays
  postGlobalHoliday: `${base.backOffice}/global-holidays/create`,
  postGlobalHolidaysList: `${base.backOffice}/global-holidays/list`,
  putGlobalHoliday: (id: string) => `${base.backOffice}/global-holidays/${id}`,
  updateGlobalHolidayStatus: (id: string) =>
    `${base.backOffice}/global-holidays/${id}/status`,
  getFetchNagerDateHolidays: `${base.backOffice}/global-holidays/fetch-and-check`,
  postGlobalHolidaysBatchImport: `${base.backOffice}/global-holidays/batch-import`,
  // Transit Times & Service Levels - Disruption Manager
  getDisruptionTypes: `${base.backOffice}/disruption-manager/disruption-types`,
  getDisruptionLevels: `${base.backOffice}/disruption-manager/disruption-levels`,
  getDisruptionCommandCenterLhTrips: `${base.backOffice}/disruption-manager/lh-trips`,
  getDisruptionCommandCenterProbills: `${base.backOffice}/disruption-manager/probills`,
  getDisruptionCommandCenterAgents: `${base.backOffice}/disruption-manager/agents`,
  getDisruptionCommandCenterEols: `${base.backOffice}/disruption-manager/eols`,
  getDisruptionCommandCenterLhJunctions: `${base.backOffice}/disruption-manager/lh-junctions`,
  getDisruptionEvents: `${base.backOffice}/disruption-manager/events`,
  postCreateDisruptionEvent: `${base.backOffice}/disruption-manager/events`,
  getDisruptionEvent: (eventId: number | string) =>
    `${base.backOffice}/disruption-manager/events/${eventId}`,
  postConfirmServiceExemption: (
    eventId: number | string,
    levelId: number | string
  ) =>
    `${base.backOffice}/disruption-manager/events/${eventId}/levels/${levelId}/override`,
  postUndoServiceExemption: (
    eventId: number | string,
    levelId: number | string
  ) =>
    `${base.backOffice}/disruption-manager/events/${eventId}/levels/${levelId}/undo-exempt`,
  postToggleLevelNotification: (
    eventId: number | string,
    levelId: number | string
  ) =>
    `${base.backOffice}/disruption-manager/events/${eventId}/levels/${levelId}/notify`,
  patchExtendDisruptionLevelDelay: (
    eventId: number | string,
    levelId: number | string
  ) =>
    `${base.backOffice}/disruption-manager/events/${eventId}/levels/${levelId}/extend`,
  postAddLevel: (eventId: number | string) =>
    `${base.backOffice}/disruption-manager/events/${eventId}/levels`,
  deleteRemoveLevel: (eventId: number | string, levelId: number | string) =>
    `${base.backOffice}/disruption-manager/events/${eventId}/levels/${levelId}`,
  postBulkDisruptionEventAction: (eventId: number | string) =>
    `${base.backOffice}/disruption-manager/events/${eventId}/bulk`,
  cancelDisruptionCommandCenterEvent: (eventId: number | string) =>
    `${base.backOffice}/disruption-manager/events/${eventId}/cancel`,
  getDisruptionCommandCenterEventAuditLog: (eventId: number | string) =>
    `${base.backOffice}/disruption-manager/events/${eventId}/audit-log`,

  // Transit Times & Service Levels - EOL Service Centers
  getEolServiceCentersList: `${base.backOffice}/eol/service-centers/list`,
  createEolServiceCenter: `${base.backOffice}/eol/service-centers/create`,
  updateEolServiceCenter: (scCode: string) =>
    `${base.backOffice}/eol/service-centers/${scCode}`,
  getEolServiceCenterDetails: (scCode: string) =>
    `${base.backOffice}/eol/service-centers/${scCode}`,
  getEolServiceCenterCarriers: `${base.backOffice}/eol/service-centers/carriers`,
  getEolLinehaulJunctions: `${base.backOffice}/eol/linehaulJunctions`,
  getEolServiceLevels: `${base.backOffice}/eol/service-centers/service-levels`,
  getEolServiceCenterZipCodesList: `${base.backOffice}/eol/service-centers/zip-codes/list`,
  exportEolServiceCenterZipCodes: `${base.backOffice}/eol/service-centers/zip-codes/export`,
  createEolServiceCenterZipCode: `${base.backOffice}/eol/service-centers/zip-codes`,
  deleteEolServiceCenterZipCode: (scCode: string, zipCodeId: number | string) =>
    `${base.backOffice}/eol/service-centers/${scCode}/zip-codes/${zipCodeId}`,
  postEolServiceCenterJunctionsList: (scCode: string) =>
    `${base.backOffice}/eol/service-centers/${scCode}/junctions/list`,
  editEolServiceCenterZipCode: (scCode: string, zipCodeId: number | string) =>
    `${base.backOffice}/eol/service-centers/${scCode}/zip-codes/${zipCodeId}`,
  getEolCriteriaDetails: (criteriaId: number) =>
    `${base.backOffice}/eol/criteria/${criteriaId}`,
  postEolServiceCenterJunction: (scCode: string) =>
    `${base.backOffice}/eol/service-centers/${scCode}/junctions`,
  getEolCriteriaById: (criteriaId: number | string) =>
    `${base.backOffice}/eol/criteria/${criteriaId}`,
  deleteEolServiceCenterJunction: (
    scCode: string,
    junctionId: number | string,
    junctionType = "origin"
  ) =>
    `${base.backOffice}/eol/service-centers/${scCode}/junctions/${junctionId}?type=${junctionType}`,
  editEolServiceCenterJunction: (scCode: string, junctionId: number | string) =>
    `${base.backOffice}/eol/service-centers/${scCode}/junctions/${junctionId}`,
  patchReorderJunctions: (
    scCode: string,
    type: "origin" | "destination" | "crossdock"
  ) =>
    `${base.backOffice}/eol/service-centers/${scCode}/junctions/reorder?type=${type}`,
  bulkExpireEolServiceCenterZipCodes: `${base.backOffice}/eol/service-centers/zip-codes/bulk-expire`,
  validateBulkExpireEolServiceCenterZipCodes: `${base.backOffice}/eol/service-centers/zip-codes/bulk-expire/validate`,
  bulkPasteEolServiceCenterZipCodes: `${base.backOffice}/eol/service-centers/zip-codes/bulk-paste`,
  validateEolServiceCenterZipCodesCsv: (scCode: string) =>
    `${base.backOffice}/eol/service-centers/${encodeURIComponent(scCode)}/zip-codes/upload-csv?preview=true`,
  bulkImportEolServiceCenterZipCodes: (scCode: string) =>
    `${base.backOffice}/eol/service-centers/${encodeURIComponent(scCode)}/zip-codes/upload-csv?preview=false`,
  getEolZipCodeServiceCenters: (
    scCode: string,
    zipCode: string,
    country: string
  ) =>
    `${base.backOffice}/eol/service-centers/${encodeURIComponent(scCode)}/zip-codes/${encodeURIComponent(zipCode)}/service-centers?country=${encodeURIComponent(country)}`,

  // Transit Times & Service Levels - Linehaul Transit Matrix
  getLinehaulTransitMatricesList: `${base.backOffice}/linehaul-transit-matrices/list`,
  getLinehaulTransitMatrixDetails: `${base.backOffice}/linehaul-transit-matrices/details`,
  getLinehaulTransitMatrixLaneOverview: `${base.backOffice}/linehaul-transit-matrices/lane-overview`,
  getLinehaulTransitMatrixLaneDetail: (
    matrixId: number | string,
    originCode: string,
    destinationCode: string
  ) =>
    `${base.backOffice}/linehaul-transit-matrices/${encodeURIComponent(String(matrixId))}/lanes/${encodeURIComponent(originCode)}/${encodeURIComponent(destinationCode)}`,
  getLinehaulTransitMatrixServiceCentersLookup: `${base.backOffice}/linehaul-transit-matrices/lookup/service-centers`,
  postDeactivateLinehaulTransitMatrix: (matrixId: number | string) =>
    `${base.backOffice}/linehaul-transit-matrices/${encodeURIComponent(String(matrixId))}/deactivate`,
  postReactivateLinehaulTransitMatrix: (matrixId: number | string) =>
    `${base.backOffice}/linehaul-transit-matrices/${encodeURIComponent(String(matrixId))}/reactivate`,
  postValidateLinehaulTransitMatrixTransitDaysConfig: `${base.backOffice}/linehaul-transit-matrices/validate-transit-days-config`,
  postValidateLinehaulTransitMatrixBulkLanesCsv: `${base.backOffice}/linehaul-transit-matrices/validate-bulk-lanes-csv`,
  postCreateLinehaulTransitMatrix: `${base.backOffice}/linehaul-transit-matrices/create`,
  putUpdateLinehaulTransitMatrix: (matrixId: number | string) =>
    `${base.backOffice}/linehaul-transit-matrices/${encodeURIComponent(String(matrixId))}`,
  getLinehaulTransitMatrixAuditLog: (matrixId: number | string) =>
    `${base.backOffice}/linehaul-transit-matrices/${encodeURIComponent(String(matrixId))}/change-log`,

  // High Cost Zip Code Schedules
  getHighCostZipCodeSchedules: `${base.backOffice}/high-cost-zip-schedules`,
  getHighCostZipCodeScheduleById: (scheduleId: number | string) =>
    `${base.backOffice}/high-cost-zip-schedules/${scheduleId}`,
  getHighCostZipCodeScheduleAccounts: `${base.backOffice}/high-cost-zip-schedules/accounts`,
  getHighCostZipCodes: `${base.backOffice}/high-cost-zip-codes`,
  getZipCodeTypes: `${base.backOffice}/high-cost-zip-codes/zip-code-types`,
  getRateStructures: `${base.backOffice}/high-cost-zip-codes/rate-structures`,
  getScopes: `${base.backOffice}/high-cost-zip-codes/scopes`,
  getAppliesTo: `${base.backOffice}/high-cost-zip-codes/applies-to`,
  getZipCodesForHighCost: (search?: string) =>
    search
      ? `${base.backOffice}/high-cost-zip-codes/zip-codes?search=${encodeURIComponent(search)}`
      : `${base.backOffice}/high-cost-zip-codes/zip-codes`,
  createHighCostZipCodeSchedule: `${base.backOffice}/high-cost-zip-schedules/create`,
  updateHighCostZipCodeSchedule: (scheduleId: number | string) =>
    `${base.backOffice}/high-cost-zip-schedules/${scheduleId}`,
  updateHighCostZipScheduleStatus: (scheduleId: number) =>
    `${base.backOffice}/high-cost-zip-schedules/${scheduleId}/status`,
  exportHighCostZipCodeScheduleAccounts: (scheduleId: number) =>
    `${base.backOffice}/high-cost-zip-schedules/${scheduleId}/accounts/export`,
  updateLaneMinimumScheduleStatus: (scheduleId: number) =>
    `${base.backOffice}/lane-minimum-schedules/${scheduleId}/status`,
  getLaneMinimumScheduleAccounts: `${base.backOffice}/lane-minimum-schedules/accounts`,
  getLaneMinimumScheduleAccountCount: (scheduleId: number) =>
    `${base.backOffice}/lane-minimum-schedules/account-count/${scheduleId}`,
  getHighCostZipCodeScheduleAccountCount: (scheduleId: number) =>
    `${base.backOffice}/high-cost-zip-schedules/account-count/${scheduleId}`,
  exportLaneMinimumScheduleAccounts: (scheduleId: number) =>
    `${base.backOffice}/lane-minimum-schedules/${scheduleId}/accounts/export`,
  exportFuelScaleAccounts: (code: string) =>
    `${base.backOffice}/fuel/scale/${code}/accounts/export`,
  getFuelScaleById: (fuelScaleId: number) =>
    `${base.backOffice}/fuel/scale/${fuelScaleId}`,
  getFuelScaleHistory: (fuelScaleId: number) =>
    `${base.backOffice}/fuel/scale/${fuelScaleId}/history`,
  downloadFuelScaleHistory: (fuelScaleId: number, format: "xlsx") =>
    `${base.backOffice}/fuel/scale/${fuelScaleId}/history/download?format=${format}`,
  createFuelScale: `${base.backOffice}/fuel/scale/create`,
  createLaneMinimumSchedule: `${base.backOffice}/lane-minimum-schedules/create`,
  updateLaneMinimumSchedule: (scheduleId: number) =>
    `${base.backOffice}/lane-minimum-schedules/${scheduleId}`,
  getCriteriaTableNames: `${base.backOffice}/fuel/scale/criteria/table`,
  getCriteriaFieldNames: (tableId: number) =>
    `${base.backOffice}/fuel/scale/criteria/fields/${tableId}`,
  getCriteriaLookupTypes: (tableId: number) =>
    `${base.backOffice}/fuel/scale/criteria/lookup/${tableId}`,
  validateFuelRatesExcel: `${base.backOffice}/fuel/scale/rates/validate-excel`,
  validateHighCostZipCodesExcel: `${base.backOffice}/high-cost-zip-codes/validate-excel`,
  validateLaneMinimumExcel: `${base.backOffice}/lane-minimums/validate-excel`,
  downloadLaneMinimumSchedule: (laneMinimumScheduleId: number) =>
    `${base.backOffice}/lane-minimums/${laneMinimumScheduleId}/download`,
  downloadHighCostZipCodeSchedule: (highCostScheduleId: number) =>
    `${base.backOffice}/high-cost-zip-codes/${highCostScheduleId}/download`,
  getCriteriaLogicOptions: `${base.backOffice}/fuel/scale/criteria/logic`,
  getCriteriaGroupLogicOptions: `${base.backOffice}/fuel/scale/criteria/group-logic`,
  getFuelScaleViewCriteria: (criteriaId: number) =>
    `${base.backOffice}/fuel/scale/criteria/${criteriaId}`,
  updateFuelScale: (fuelScaleId: number) =>
    `${base.backOffice}/fuel/scale/${fuelScaleId}`,
  updateFuelScaleStatus: (fuelScaleId: number) =>
    `${base.backOffice}/fuel/scale/${fuelScaleId}/status`,
  getFuelPriceList: `${base.backOffice}/fuel-price/list`,
  getFuelPrice: `${base.backOffice}/fuel-price`,
  getFuelPriceHistory: `${base.backOffice}/fuel-price/history`,
  getFuelSurchargesList: `${base.backOffice}/global-rates/fuel-scales/list`,
  getGlobalRatesFuelSurchargeOptions: `${base.backOffice}/global-rates/fuel-scale/options`,
  getGlobalRatesFuelSurchargeSequenceOptions: `${base.backOffice}/global-rates/fuel-scale/sequence/options`,
  addFuelSurcharge: `${base.backOffice}/global-rates/fuel-scales`,
  editFuelSurcharge: (scheduleId: number) =>
    `${base.backOffice}/global-rates/fuel-scales/${scheduleId}`,
  getGlobalRatesLaneMinimumsList: `${base.backOffice}/global-rates/lane-minimum-schedule/list`,
  createGlobalRatesLaneMinimum: `${base.backOffice}/global-rates/lane-minimum-schedule`,
  updateGlobalRatesLaneMinimum: (id: number | string) =>
    `${base.backOffice}/global-rates/lane-minimum-schedule/${id}`,
  getGlobalRatesHighCostZipCodesList: `${base.backOffice}/global-rates/high-cost-schedule/list`,
  getGlobalRatesHighCostZipCodesOptions: `${base.backOffice}/global-rates/high-cost-schedule/options`,
  updateGlobalRatesHighCostZipCode: (scheduleId: number | string) =>
    `${base.backOffice}/global-rates/high-cost-schedule/${scheduleId}`,
  getGlobalRatesLaneMinimumScheduleOptions: `${base.backOffice}/global-rates/lane-minimum-schedule/options`,
  getGlobalRatesRateBaseList: `${base.backOffice}/global-rates/rate-base-schedule/list`,
  getGlobalRatesRateBaseOptions: `${base.backOffice}/global-rates/rate-base/options`,
  getGlobalRatesRateBaseSequenceOptions: `${base.backOffice}/global-rates/rate-base/sequence/options`,
  createGlobalRatesRateBase: `${base.backOffice}/global-rates/rate-base-schedule`,
  updateGlobalRatesRateBase: (scheduleId: number) =>
    `${base.backOffice}/global-rates/rate-base-schedule/${scheduleId}`,
  createGlobalRatesHighCostZipCode: `${base.backOffice}/global-rates/high-cost-schedule`,
  getGlobalRatesAccessorialScheduleList: `${base.backOffice}/global-rates/accessorial-schedule/list`,
  getGlobalRatesAccessorialScheduleOptions: `${base.backOffice}/global-rates/accessorial-schedule/options`,
  createGlobalRatesAccessorialSchedule: `${base.backOffice}/global-rates/accessorial-schedule`,
  getGlobalRatesHistoryCategories: `${base.backOffice}/global-rates/history/categories`,
  getGlobalRatesHistory: `${base.backOffice}/global-rates/history`,
  updateGlobalRatesAccessorialSchedule: (id: number) =>
    `${base.backOffice}/global-rates/accessorial-schedule/${id}`,
  updateFuelPrice: (fuelPriceId: number) =>
    `${base.backOffice}/fuel-price/${fuelPriceId}`,
  retryFuelPrice: (fuelPriceId: number) =>
    `${base.backOffice}/fuel-price/${fuelPriceId}/retry`,
  downloadFuelPriceHistory: (fuelPriceId: number, format: "xlsx" | "csv") =>
    `${base.backOffice}/fuel-price/${fuelPriceId}/history/download?format=${format}`,
  downloadFuelRates: (fuelScaleId: number) =>
    `${base.backOffice}/fuel/scale/${fuelScaleId}/rates/download?format=xlsx`,
  getBillers: (searchTerm: string) =>
    `${base.backOffice}/billing-queue/billers?search=${encodeURIComponent(searchTerm)}`,
  assignBiller: `${base.backOffice}/billing-queue/assign-biller`,
  getAccessorialChargesList: `${base.backOffice}/accessorial-charges/list`,
  getAccessorialChargeById: (id: number) =>
    `${base.backOffice}/accessorial-charges/${id}`,
  updateAccessorialCharge: (id: number) =>
    `${base.backOffice}/accessorial-charges/${id}`,
  getAccessorialChargeCriteria: (criteriaId: number) =>
    `${base.backOffice}/accessorial-charges/criteria/${criteriaId}`,
  getAccessorialChargeRateTypes: `${base.backOffice}/accessorial-charges/rate-type`,
  getAccessorialChargeMovementTypes: `${base.backOffice}/accessorial-charges/movement-type`,
  getAccessorialChargeCurrencyTypes: `${base.backOffice}/accessorial-charges/currency-type`,
  getAccessorialChargeAccessorialTypes: `${base.backOffice}/accessorial-charges/accessorial-type`,
  getAccessorialChargeUnitTypes: `${base.backOffice}/accessorial-charges/unit-type`,
  getAccessorialChargeCalcTypes: `${base.backOffice}/accessorial-charges/calc-type`,
  getAccessorialChargeInstructionCodes: `${base.backOffice}/accessorial-charges/instruction-codes`,
  createAccessorialCharge: `${base.backOffice}/accessorial-charges`,
  updateAccessorialChargeStatus: (id: number) =>
    `${base.backOffice}/accessorial-charges/${id}/status`,
  getAccessorialSchedulesList: `${base.backOffice}/accessorial-schedules/list`,
  getAccessorialScheduleById: (id: number) =>
    `${base.backOffice}/accessorial-schedules/${id}`,
  getAccessorialChargesForSelection: `${base.backOffice}/accessorial-charges/selection`,
  getSelectedAccessorialChargesDetails: `${base.backOffice}/accessorial-charges/selected`,
  createAccessorialSchedule: `${base.backOffice}/accessorial-schedules`,
  updateAccessorialSchedule: (scheduleId: number) =>
    `${base.backOffice}/accessorial-schedules/${scheduleId}`,
  updateAccessorialScheduleStatus: (scheduleId: number) =>
    `${base.backOffice}/accessorial-schedules/${scheduleId}/status`,
  getAccessorialScheduleAccounts: `${base.backOffice}/accessorial-schedules/accounts`,
  exportAccessorialScheduleAccounts: (scheduleId: number) =>
    `${base.backOffice}/accessorial-schedules/${scheduleId}/accounts/export`,
  getRateBasesList: `${base.backOffice}/rate-bases/list`,
  createRateBase: `${base.backOffice}/rate-bases`,
  updateRateBase: (id: number) => `${base.backOffice}/rate-bases/${id}`,
  getRateBaseTypes: `${base.backOffice}/rate-bases/rate-base-types`,
  getRateBaseCriteria: (criteriaId: number) =>
    `${base.backOffice}/rate-bases/criteria/${criteriaId}`,
  getRateBaseDetail: (id: number) => `${base.backOffice}/rate-bases/${id}`,
  getRateBaseThresholds: (id: number) =>
    `${base.backOffice}/rate-bases/${id}/thresholds`,
  updateRateBaseStatus: (id: number) =>
    `${base.backOffice}/rate-bases/${id}/status`,
  downloadRateBase: (id: number) =>
    `${base.backOffice}/rate-bases/${id}/download`,
  getRateTariffs: `${base.backOffice}/rate-bases/rate-tariffs`,
  getRateBasesHandlingUnits: `${base.backOffice}/rate-bases/handling-units`,
  getRateBasesHandlingTypeOptions: `${base.backOffice}/rate-bases/handling-type-options`,
  getRateBasesOverageRuleOptions: `${base.backOffice}/rate-bases/overage-rule-options`,
  validateFlatRateExcel: `${base.backOffice}/rate-bases/validate-flat-rate-excel`,
  validateSingleRateLaneExcel: `${base.backOffice}/rate-bases/validate-single-rate-lane-excel`,
  validateTruckloadLaneExcel: `${base.backOffice}/rate-bases/validate-truckload-lane-excel`,
  validateUnitCountRateLaneExcel: `${base.backOffice}/rate-bases/validate-unit-count-rate-lane-excel`,
  validateAvgWeightRateLaneExcel: `${base.backOffice}/rate-bases/validate-average-weight-rate-lane-excel`,
  validateWeightUnitCountRateLaneExcel: `${base.backOffice}/rate-bases/validate-weight-unit-count-rate-lane-excel`,
  validateCommodityRateExcel: `${base.backOffice}/rate-bases/validate-commodity-rate-excel`,
  validateMileageRateExcel: `${base.backOffice}/rate-bases/validate-mileage-rate-lane-excel`,
  getRateBaseAccounts: `${base.backOffice}/rate-bases/accounts`,
  exportRateBaseAccounts: (rateBaseId: number) =>
    `${base.backOffice}/rate-bases/${rateBaseId}/accounts/export`,
  getRulesList: `${base.backOffice}/rules/list`,
  getRuleTypes: `${base.backOffice}/rules/rule-types`,
  getRateClasses: `${base.backOffice}/rules/rate-classes`,
  getRuleDetail: (id: number) => `${base.backOffice}/rules/${id}`,
  getRuleNextVersion: (ruleTypeId: number) =>
    `${base.backOffice}/rules/next-version?ruleTypeId=${ruleTypeId}`,
  createRule: `${base.backOffice}/rules/create`,
  updateRule: (ruleId: number) => `${base.backOffice}/rules/${ruleId}`,
  deleteRule: (ruleId: number) => `${base.backOffice}/rules/${ruleId}`,
} as const;

// TODO: Use this type to model our API endpoints via a consistent config to enforce consistency,
// streamline code, and support custom devtools.
// export type Endpoint<TResponse> = {
//   baseUrl: "old" | "new";
//   pathname: string;
//   search?: string;
//   method: "GET" | "POST" | "PUT" | "DELETE";
//   responseSchema: unknown;
//   mockResponses: MockResponse<TResponse>[];
// };

// type MockResponse<T> = {
//   status: number;
//   response: T;
// };
